// Central editor state: one reducer, one document. The `doc` field is the
// serializable scene (what export/share persists); everything else is
// session-scoped.

import type {
  RoomDims, ModalPipeline, Furnishing, SpeakerSubkind, SpeakerRole,
  Vec3, PanelSize, MaterialsResponse, AdminProduct, TreatmentTier,
  FreqResponseResponse, RT60Response, SoundstageResponse,
  RoomShape, Corner, WingSpec, CeilingProfile, AxisProfile, CeilingNode,
  RevealLevel, RevealIndices, ProductSummary,
  SpeakerLayout, OptimizeCandidate,
  ModalResponse, ModalRefResponse, RaytraceResponse, RaytraceRefResponse,
  ModalVolumeSweepResponse, VolumeSweepRefResponse,
  ModalVelocityResponse, VelocityRefResponse,
  JobOrigin,
} from './adminTypes';
import type { Units } from './units';
import { vizFromPersisted } from './persistedViz';
import { applyScannedGeometry } from './scanGeometry';

/** The four viz layers a Compute (`analyze_scene`) returns, single-valued —
 *  see `EditorState['analysis'].viz`'s own doc comment. Every layer accepts
 *  EITHER the legacy inline shape (a v1-persisted room, or a pre-W2/W5
 *  in-memory response) or the artifact-referenced shape a fresh
 *  `analyze_scene` response carries (job pipeline W5 added `modal`/
 *  `raytrace` to the set already true of `volume`/`velocity`) — consumers
 *  go through `resolveVolumeBytes`/`resolveModal`/`resolveRaytrace`
 *  (`@roomtreatment/api-client`) to decode either. */
export interface AnalysisViz {
  modal: ModalResponse | ModalRefResponse | null;
  volume: ModalVolumeSweepResponse | VolumeSweepRefResponse | null;
  velocity: ModalVelocityResponse | VelocityRefResponse | null;
  raytrace: RaytraceResponse | RaytraceRefResponse | null;
}
import { floorFinishAcoustic } from './floorFinish';
import { faceToward } from './speakerFacing';
import {
  bottomHeightFromCenterY, centerYFromBottomHeight, clampBottomHeight,
  seatOnFloor, speakerCabinetH, standsOnFloor,
} from './speakerHeight';
import { DESIGN_COPY, copyLabel, nextBlankLabel, uniqueLabel } from './designCopy';
import { reseatCornerTraps } from './polygonCorners';
import {
  type WindowVertex,
  syncWindowGeometry,
} from './windowGeom';
import { syncDoorGeometry } from './doorGeom';
import {
  type MediaAspect,
  type MediaGeom,
  type MediaKind,
  clampDiagonalM,
  mediaDeviceSize,
} from './mediaTypes';

export type { WindowVertex };
export type ObjectKind = 'speaker' | 'listener' | 'panel' | 'furniture' | 'window' | 'door' | 'media' | 'gear';
export type VizMode = 'none' | 'modal' | 'raytrace' | 'volumetric' | 'velocity'
  | 'reflections';
/**
 * How the room itself is drawn, independent of which field is overlaid on it.
 *
 * 'design'  — the room as designed: paint, floor finishes, furniture models.
 * 'physics' — the room as the solver sees it: every physical object one flat
 *             white, so nothing decorative competes with the field drawn over
 *             it. Outlines and the wireframe survive (they carry form, not
 *             colour), and so do the two objects whose hue is load-bearing —
 *             see PHYSICS_EXEMPT in web's scene/renderMode.ts.
 */
export type RenderMode = 'design' | 'physics';
export type ModalView = 'topdown' | 'walls';
export type PanelMounting = 'wall' | 'ceiling' | 'floor';
export type PanelShape = 'flat' | 'corner';
export type CompareMode = 'before' | 'after';

export interface SpeakerEntry {
  id: string;
  subkind: SpeakerSubkind;
  role: SpeakerRole;
  freqResponse: [number, number][] | null;
  position: Vec3;
  rotationY: number;
  /** Cabinet finish override. Undefined means the default ink (see
   *  SPEAKER_COLORS in web/scene/constants.ts) — this is a per-instance
   *  user customization, not wire/physics data, same relationship
   *  PanelEntry.color has to a panel's default fill. */
  color?: string;
  /** Undefined or true: the speaker's yaw is a LOCKED function of its
   *  position and the listener's — every SYNC_TRANSFORM recomputes it via
   *  `faceToward`, so dragging the speaker also turns it toward the seat.
   *  False: the user has frozen the current yaw (the speaker card's "Face
   *  listener" checkbox), and SYNC_TRANSFORM leaves rotationY exactly as
   *  passed. Undefined (not false) is today's behaviour, so no document
   *  migration is needed. */
  faceListener?: boolean;
  /** Cabinet size override, metres. Undefined means the subkind's default
   *  box (SPEAKER_SIZE in web/scene/constants.ts) — this is a per-instance,
   *  VISUAL-ONLY customization: the solver has no physical speaker geometry
   *  (speakerToWire sends no size), so resizing a cabinet here never touches
   *  the physics. Same "undefined = today's behaviour" relationship as
   *  `color`/`faceListener` above. */
  size?: PanelSize;
  /** Gear catalogue product this speaker is (`@roomtreatment/av-catalog` id).
   *  Undefined is a generic speaker. Binding a product also sets `size` to the
   *  product's published dimensions; like `size`, it is visual and build data,
   *  never sent to the solver. */
  productId?: string;
}

/**
 * A receiver, processor or amplifier from the Gear catalogue, placed in the
 * room (usually on a media console). It has no acoustic effect and is never
 * sent to the solver; its position only feeds the build checks (cable runs).
 */
export interface GearEntry {
  id: string;
  /** `@roomtreatment/av-catalog` product id. */
  productId: string;
  /** Bottom-centre of the unit, metres. */
  position: Vec3;
  /** Yaw; the unit's front faces local +Z. */
  rotationY: number;
}

export interface ListenerEntry {
  id: string;
  position: Vec3;
  rotationY: number;
}

/** One corner of a tapered panel's plan-view footprint, panel-local metres.
 *  Structurally identical to @roomtreatment/panel-taper's `TaperPoint` —
 *  duplicated (not imported) so `core` stays a dependency-free leaf package;
 *  panel-taper's own tests are what actually exercise this shape's math. */
export interface PanelTaperPoint { x: number; z: number; }

/** A tapered panel's four independently-positioned corners — see
 *  @roomtreatment/panel-taper for the full contract (mutual-clamp so the
 *  quad can never self-intersect, edge-length/angle helpers, the
 *  bounding-size derivation that keeps `PanelSize.w`/`.d` safe for every
 *  existing boundary clamp). `fl`/`fr`/`bl`/`br` = front-left / front-right /
 *  back-left / back-right, front being the room-facing (local +Z) edge. */
export interface PanelTaper {
  fl: PanelTaperPoint;
  fr: PanelTaperPoint;
  bl: PanelTaperPoint;
  br: PanelTaperPoint;
}

export interface PanelEntry {
  id: string;
  sku: string | null;
  label: string | null;
  subkind: string | null;
  size: PanelSize;
  mounting: PanelMounting;
  position: Vec3;
  rotation: Vec3;
  color?: string;
  shape?: PanelShape;
  airGap?: number;
  sigma?: number;
  /** True once the user has detached this panel from its mounting surface —
   *  it becomes a free rigid body (ground-plane drag + card-based Y/yaw
   *  controls) instead of sliding on a wall/ceiling/floor/corner. Undefined
   *  or false is today's surface-glued behaviour; no document migration
   *  needed. See usePanelPlacement's detach()/attach(). */
  detached?: boolean;
  /** Set only for a FLAT panel that's been tapered via the "Customize
   *  dimensions" editor — undefined means a plain box, today's behaviour
   *  exactly. Client-side/visual only: never sent to the backend (the wire
   *  format and the absorption-curve physics only ever need the bounding
   *  `size`, which stays in sync via panel-taper's taperBoundingSize). Not
   *  applicable to corner traps (shape:'corner' derives its wedge from the
   *  seated corner's interior angle — never stored, never CAD-edited). */
  taper?: PanelTaper;
}

export interface WindowEntry {
  id: string;
  /** `surfaceKey` of the host wall/ceiling. */
  surface: string;
  u: number;
  v: number;
  w: number;
  h: number;
  /** CCW outline in the host (u, v) frame. Absent ⇒ rectangle from u,v,w,h. */
  polygon?: WindowVertex[];
  /** Glass/frame thickness into the room, metres. Default 0.02. */
  thickness?: number;
  /**
   * Catalog material id. Default `glass_heavy`.
   * TODO(glass-curve): thinner / IGU once measured octave bands exist.
   */
  material?: string;
  /** Frame border width inside the opening, metres. Visual-only (3D trim); the solver still sees the whole opening as glass. Default 0.05, max 0.20. */
  frameWidth?: number;
  /** WINDOW_FRAME_OPTIONS id. Default 'wood'. Visual-only like frameWidth. */
  frameMaterial?: string;
}

/**
 * A door is the same host-wall opening stamp as `WindowEntry`, with the
 * sill pinned to the floor (`v = h / 2`, enforced by `syncDoorGeometry` in
 * `doorGeom.ts` — never set independently). `variant` only picks which GLB
 * layout renders; a closed double door absorbs identically to a closed
 * single door of the same w×h.
 */
export interface DoorEntry extends WindowEntry {
  variant: 'single' | 'double';
}

/** Where a projector throws. Only the placement lives here — the picture's
 *  diagonal and aspect are on the owning `MediaEntry`, because they describe
 *  the same picture whether it's emitted by a panel or thrown at a wall. */
export interface MediaScreen {
  /** `surfaceKey` of the host wall. */
  surface: string;
  /** Screen centre in the host wall's (u, v) frame — u along the wall,
   *  v above the floor. Same frame `WindowEntry` uses. */
  u: number;
  v: number;
  /** Woven or perforated screen: sound passes through it, so speakers can sit
   *  behind it and it contributes no reflector. Suppresses the screen's OBB on
   *  the wire — see the media plan's 6.2 for why that's the honest first
   *  approximation and what a fuller model would need. */
  acousticallyTransparent?: boolean;
  /** Gear catalogue screen model (`projector_screen`). Undefined is a generic
   *  screen. Build data, never sent to the solver: what reaches it is the
   *  picture size, which binding a model sets on the owning entry. */
  productId?: string;
}

/**
 * A TV or a projector setup.
 *
 * Placement mirrors `PanelEntry` exactly: `position`/`rotation` are the world
 * transform and the mounting surface is DERIVED from them (see
 * `@roomtreatment/panel-surface`'s `inferSurface`), never stored — so solver
 * output, shared documents and hand-placed objects all read the same way with
 * no schema migration. `detached` and `standoff` play the same roles
 * `PanelEntry.detached` and `PanelEntry.airGap` do.
 */
export interface MediaEntry extends MediaGeom {
  id: string;
  kind: MediaKind;
  label: string | null;
  /** The picture this device makes, corner to corner, metres. For a TV that is
   *  the panel itself; for a projector it is the image on `screen`. Width and
   *  height are always derived from it (see mediaTypes), never stored. */
  diagonalM: number;
  aspect: MediaAspect;
  /** The DEVICE's transform — the panel for a TV, the body for a projector.
   *  A projector's picture is not where the projector is. */
  position: Vec3;
  rotation: Vec3;
  /** Free rigid body rather than surface-glued: a TV on a stand, a projector
   *  on a rear shelf. Undefined or false is surface-mounted. */
  detached?: boolean;
  /** Distance off the host surface, metres. A wall TV's mount standoff; a
   *  ceiling projector's drop-rod length. */
  standoff?: number;
  /** Projector only. Absent means it throws at nothing yet — allowed, since
   *  the device is still a real object in the room. */
  screen?: MediaScreen;
  /** Gear catalogue model of the DEVICE (`tv` or `projector`). Undefined is a
   *  generic one. The room's display and the System view's display are this
   *  one entry: a TV model picked in the parts list binds here (and sets
   *  `diagonalM` from its published size) rather than adding a second TV.
   *  Build data, never sent to the solver. */
  productId?: string;
}

/**
 * A media entry seen the way the surface-placement code sees it.
 *
 * `inferSurface`, `transformToPose` and the rest of `@roomtreatment/panel-surface`
 * take a `PanelEntry` but read only position, rotation, size and `detached`
 * off it — and a TV is mounted exactly the way a panel is. So media borrows
 * that whole path through this one adapter rather than growing a parallel
 * copy of the placement maths. The remaining fields are inert padding, never
 * read by anything downstream of the mounting branch.
 *
 * One function, not one per caller: MediaObject, useObjectDrag and (next)
 * useMediaPlacement all need exactly this, and three hand-copies of it is the
 * duplication `panelsClearedOf` was extracted to end.
 */
export function mediaAsMountable(entry: MediaEntry): PanelEntry {
  return {
    id: entry.id,
    sku: null,
    label: entry.label,
    subkind: null,
    size: mediaDeviceSize(entry.kind, entry),
    mounting: 'wall',
    position: entry.position,
    rotation: entry.rotation,
    detached: entry.detached,
  };
}

export interface FurnitureEntry {
  id: string;
  presetId: string;
  size: PanelSize;
  position: Vec3;
  rotation: Vec3;
  /** Catalog furniture material override. Absent ⇒ preset default on the wire. */
  material?: string;
}

export type MainSpeakerType = 'bookshelf' | 'floorstanding';

export interface WingEntry extends WingSpec { id: string }

export interface NoGoZone { id: string; x0: number; y0: number; x1: number; y1: number }

export interface FreqRange { low: number; high: number }

export interface SpeakerSetup {
  layout: SpeakerLayout;
  sub: number;
  mainType: MainSpeakerType;
  headFreq: FreqRange | null;
  subFreq: FreqRange | null;
  overheadFreq: FreqRange | null;
}

export interface ScanMetadata {
  capturedAt?: string;
  deviceModel?: string;
  confidence?: 'low' | 'medium' | 'high';
  rawCaptureRef?: string;
}

export interface EditorDoc {
  dims: RoomDims;
  floorMaterial: string | null;
  wallMaterial: string | null;
  furnishing: Furnishing;
  speakers: SpeakerEntry[];
  listener: ListenerEntry | null;
  panels: PanelEntry[];
  furniture: FurnitureEntry[];
  roomShape: RoomShape;
  footprint: Corner[] | null;
  wings: WingEntry[];
  ceiling: CeilingProfile | null;
  surfaceColors?: Record<string, string>;
  /** Acoustic catalog id per `surfaceKey`. Missing → global wall/floor material. */
  surfaceMaterials?: Record<string, string>;
  /** The floor's visible finish — see FLOOR_FINISHES. Absent means the floor is
   *  painted rather than surfaced, which is how every room saved before
   *  finishes existed loads. Setting one also sets the floor's ACOUSTIC
   *  material, since a finish carries both (SET_FLOOR_FINISH). */
  floorFinish?: string | null;
  windows?: WindowEntry[];
  /** Doors. Optional like `windows`, so every room saved before doors
   *  existed stays valid, read as `doc.doors ?? []`. */
  doors?: DoorEntry[];
  /** TVs and projector setups. Optional like `windows`, so every room saved
   *  before media existed stays a valid document with no migration. Read it
   *  as `doc.media ?? []`. */
  media?: MediaEntry[];
  /** Receivers, processors and amplifiers. Optional like `media`; read as
   *  `doc.gear ?? []`. Never a display: a TV, projector or screen model binds
   *  to `media` (rooms saved while displays were gear are moved there by
   *  ADOPT_GEAR_DISPLAYS). */
  gear?: GearEntry[];
  /** Ticked Gear checklist items, by item id (`@roomtreatment/av-compat`). */
  checklist?: { done: string[] };
  /** User-set loudness target for gear sizing (`@roomtreatment/av-compat`
   *  `roomRequirements`). Absent means the default of 95 dB peaks
   *  ("Movie night") — readers apply that default, it is never migrated in.
   *  Excluded from `sceneContentKey`/`docHash` like `checklist`: it never
   *  reaches the solver. */
  listening?: { peakDb: number };
  /** What the owner means to spend on this design, in whole US dollars. A
   *  design constraint, not a physics input: absent (or null) means no budget
   *  was set, and nothing assumes one. Excluded from `sceneContentKey`/
   *  `docHash` like `listening`, because it never reaches the solver. */
  budget?: { usd: number } | null;
  /** The order — where each part is bought and what the owner will really
   *  pay for it (the System view's Order). Like `budget`, a buying
   *  decision, never a physics input: excluded from `sceneContentKey`, and
   *  absent means every line at its defaults (list price, the default store,
   *  no promo or shipping, no tax). */
  order?: OrderState | null;
  noGoZones: NoGoZone[];
  facingYawRad: number | null;
  speakerSetup: SpeakerSetup;
  editorMode?: 'guided' | 'advanced';
  guidedStep?: string;
  source?: 'manual' | 'lidar_scan' | 'video_scan';
  scanMetadata?: ScanMetadata;
}

export function headCountForLayout(layout: SpeakerLayout): number {
  if (layout === 'mono') return 1;
  if (layout === 'stereo' || layout === 'stereo.2' || layout === 'stereo.4') return 2;
  if (layout === '5.1' || layout === '5.1.2' || layout === '5.1.4') return 5;
  return 7;
}

export function overheadCountForLayout(layout: SpeakerLayout): number {
  if (layout === 'stereo.2' || layout === '5.1.2' || layout === '7.1.2') return 2;
  if (layout === 'stereo.4' || layout === '5.1.4' || layout === '7.1.4') return 4;
  return 0;
}

/** The inverse of `headCountForLayout` / `overheadCountForLayout`: the
 *  layout enum for a given (head, atmos) pair. Used by
 *  `@roomtreatment/room-flow`'s `planSpeakerGroupAdd` to widen a doc's
 *  declared layout by the minimum amount a new channel group needs (e.g. a
 *  lone center on a stereo pair needs a bed that has a `C` slot — see
 *  av-compat's `bedSlotsFor` — which means at least a 5.1 head count) while
 *  preserving whichever atmos count was already declared. `head`/`atmos`
 *  are read loosely (`<=` bands, matching the two forward functions' own
 *  style) so a caller doesn't have to pre-clamp to the exact literal set. */
export function layoutFromCounts(head: number, atmos: number): SpeakerLayout {
  const suffix = atmos >= 4 ? '.4' : atmos >= 2 ? '.2' : '';
  if (head <= 1) return 'mono';
  if (head <= 2) return `stereo${suffix}` as SpeakerLayout;
  if (head <= 5) return `5.1${suffix}` as SpeakerLayout;
  return `7.1${suffix}` as SpeakerLayout;
}

/** The largest budget a design can carry, in dollars. A guard against a stray
 *  keystroke, not a statement about anyone's system. */
export const MAX_BUDGET_USD = 1_000_000;

/** One line of the order: where it is bought and what is paid for it beyond
 *  the list price. An absent field is that field's default. */
export interface OrderLine {
  /** A retailer id (`@roomtreatment/av-catalog`'s RETAILERS) — a plain
   *  string here, so the document stays free of the catalogue. */
  where?: string;
  /** What one lot (a pair, or one) really costs, USD — the owner's price
   *  in place of the list price. */
  lotUsd?: number;
  /** Discount on the whole line, USD. */
  promoUsd?: number;
  /** Shipping for the whole line, USD. */
  shippingUsd?: number;
}

export interface OrderState {
  /** Sales tax, percent, on each line's price after its promo. */
  taxPct?: number;
  /** By order key: a product id, or `wire` / `sub-cable`. */
  lines?: Record<string, OrderLine>;
}

/** The highest sales tax the order takes, percent — a guard, like the budget's. */
export const MAX_TAX_PCT = 25;

const moneyOrNull = (v: number | null | undefined): number | undefined => (
  v == null || !Number.isFinite(v) ? undefined : Math.round(Math.min(MAX_BUDGET_USD, Math.max(0, v)) * 100) / 100
);

const OPTIMIZE_SUBFLOW_STEPS = new Set([
  'subset', 'placement', 'place-fixed', 'distance', 'nogo', 'generate',
]);

export function sceneContentKey(doc: EditorDoc): string {
  const scene: EditorDoc = { ...doc };
  delete scene.editorMode;
  delete scene.guidedStep;
  // Gear electronics, checklist ticks and the listening-level target never
  // reach the solver: changing them must not mark a solve as computed for an
  // older document.
  delete scene.gear;
  delete scene.checklist;
  delete scene.listening;
  delete scene.budget;
  delete scene.order;
  // A display's model is build data; its size (which a model sets) is what
  // the solver sees, and that stays in.
  if (scene.media) scene.media = scene.media.map(physicalMedia);
  return JSON.stringify(scene);
}

/** A media entry as the solver sees it: without the models bound to it. */
function physicalMedia(m: MediaEntry): MediaEntry {
  const { productId: _device, screen, ...rest } = m;
  if (!screen) return rest;
  const { productId: _screen, ...place } = screen;
  return { ...rest, screen: place };
}

/** FNV-1a, 32-bit, hex-encoded (8 chars). Deterministic, dependency-free,
 *  and fast enough to run on every job submit — this is a content
 *  fingerprint for "is this the same document", never anything
 *  security-sensitive (unlike a share token). */
function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Job pipeline W3 (2026-09-06), §3.1: `origin.docHash` — a stable, compact
 *  hash of the fields that affect physics. `sceneContentKey` already covers
 *  the whole doc (minus the two UI-only fields, `editorMode`/`guidedStep`,
 *  which never change what the solver sees) with a stable JSON stringify,
 *  so this reuses it rather than inventing a second signature scheme
 *  (`geometrySignature`, by contrast, deliberately covers ONLY geometry —
 *  it excludes materials/speakers/panels, which also affect physics, so it
 *  cannot stand in for this). Two docs that render identically to the
 *  solver hash identically; anything that would change freq/rt60/
 *  soundstage/placement changes the hash. Used to detect "this result was
 *  computed for an OLDER version of this design" on job apply — see
 *  `APPLY_ROOM_SOLVE`/`COMMIT_PLACEMENT_SIM`'s origin handling in
 *  `editorReducer` below. */
export function docHash(doc: EditorDoc): string {
  return fnv1aHex(sceneContentKey(doc));
}

/**
 * `iterations`, when given, is the room's full Design list (not just the
 * requested/active one) — once ANY Design in a room has graduated to the
 * advanced editor, no Design of that room should ever show onboarding
 * again, including one that individually still looks "fresh" (its own
 * doc.editorMode unset, e.g. the root Design when a fork or a later Design
 * is what actually finished onboarding first). Without this, switching
 * Design tabs — or reloading on a Design other than the one that
 * graduated — could resurface onboarding for a room the user has already
 * been editing in the real editor.
 */
export function resumeRoomMode(doc?: EditorDoc, iterations?: DesignListState): 'guided' | 'advanced' {
  if (!doc) return 'guided';
  if (doc.editorMode === 'advanced') return 'advanced';
  if (iterations?.items.some((it) => it.doc.editorMode === 'advanced')) return 'advanced';
  if (doc.guidedStep) return 'guided';
  return doc.panels && doc.panels.length > 0 ? 'advanced' : 'guided';
}

export function resumeGuidedStep(doc?: EditorDoc): string {
  const saved = doc?.guidedStep;
  if (saved) return OPTIMIZE_SUBFLOW_STEPS.has(saved) ? 'optimize' : saved;
  return doc && doc.panels && doc.panels.length > 0 ? 'result' : 'intro';
}

export type EditorDialog =
  | { kind: 'channel'; speakerId: string }
  | { kind: 'freqCurve'; speakerId: string }
  | { kind: 'panelPlan'; surface: 'floor' | 'ceiling' }
  | { kind: 'treatment' }
  | { kind: 'products' }
  | { kind: 'share'; url: string; token: string }
  | { kind: 'signupToSave' }
  | { kind: 'constraintZone' }
  /** "Customize dimensions" — re-edits an already-placed flat panel's taper
   *  via PanelPlacementCard. CustomPanelDesigner's own PRE-placement draft
   *  uses the same PanelDimensionEditor component directly, as a local
   *  modal — it has no `doc.panels` entry yet for this global,
   *  Redux-driven dialog to address. */
  | { kind: 'panelDimensions'; panelId: string };

export interface EditorState {
  doc: EditorDoc;
  settings: { pipeline: ModalPipeline; units: Units };
  catalog: {
    materials: MaterialsResponse | null;
    products: AdminProduct[] | null;
    loading: boolean;
    error: string | null;
  };
  view: {
    vizMode: VizMode;
    /** Design vs. physics rendering of the room — see RenderMode. Shared
     *  editor chrome like the rest of `view`, so it survives a Design switch
     *  and is never part of a saved document. */
    renderMode: RenderMode;
    modalFreq: number;
    /** Which velocity band the particle-velocity cloud is showing, as a
     *  position in 0..1 across THIS room's auto-exposed range (not an
     *  absolute speed — the field is normalised per room). 1 = the fastest
     *  air, which is where the view opens because it is the actionable end. */
    velocityLevel: number;
    modalView: ModalView;
    vizStatus: string;
    dragging: boolean;
    compare: CompareMode;
    hoverFreq: number | null;
    placingSku: string | null;
    selectedSurface: string | null;
    reflectionMaxTimeMs: number;
    reflectionOrder: number | null;
  };
  selection: { id: string; kind: ObjectKind } | null;
  analysis: {
    freq: FreqResponseResponse | null;
    rt60: RT60Response | null;
    soundstage: SoundstageResponse | null;
    loading: boolean;
    error: string | null;
    token: number;
    scanNonce: number;
    scannedToken: number;
    lastSolveMs: number | null;
    voxelArmedSig: string | null;
    /** Job pipeline W2 (2026-09-06): the four viz layers a Compute
     *  (`analyze_scene`) returns alongside freq/rt60/soundstage, single-
     *  valued — this always describes the CURRENT scene (there is no
     *  separate before/after pair here; see `PersistedVizPayloadV2`'s
     *  header for why). Written wholesale by `ANALYSIS_DONE` and by
     *  `analysisFromResults` on load/hydrate; left untouched by every
     *  other action (including `applyStaleClass`) — a stale scene does NOT
     *  null this out, it just means `analysisMatchesScene(analysis)` is
     *  false, which is what gates a consumer from trusting it as current.
     *  `null` means "never computed for this Design yet". */
    viz: AnalysisViz | null;
    /** True whenever the untreated (panels: []) baseline this room's Graphs
     *  and viz layers are compared against may no longer match the current
     *  room — set by any non-panel-only edit (SET_DIMS, furniture, speakers,
     *  room shape, ...). A panel-only edit leaves this false: the bare room
     *  didn't change, so the last "before" result is still valid. */
    beforeStale: boolean;
    /** True whenever the treated (real-panels) result may no longer match
     *  the current room — set by EVERY sim-dirtying edit, panel-only edits
     *  included (moving a panel invalidates the treated result but not the
     *  untreated one). Cleared the moment a fresh treated result lands
     *  (ANALYSIS_DONE, TREATMENT_TIER_DONE). `beforeStale` implies this is
     *  also true — there is never a state where the room changed but only
     *  the untreated side needs a refresh. */
    afterStale: boolean;
    /** Bumped every time a fresh "after" (treated) result lands — the signal
     *  a dependent hook (e.g. usePanelAbsorption) can put in a useEffect
     *  dependency array instead of watching `treatment.results[activeTier]`,
     *  which only the wizard ever wrote. See ANALYSIS_DONE and
     *  applyPlacementSim (TREATMENT_TIER_DONE) — both increment it. */
    afterResultVersion: number;
  };
  treatment: {
    running: Record<TreatmentTier, boolean>;
    results: Record<TreatmentTier, TierResult | null>;
    activeTier: TreatmentTier;
    activeReveal: RevealLevel;
    error: string | null;
  };
  ui: { dialog: EditorDialog | null };
  history: EditorHistory;
  iterations: DesignListState;
}

/** One live Design of the room — independently editable, not a frozen
 *  snapshot (see FORK_ITERATION). `doc`/`history`/`analysis`/`treatment`
 *  are per-Design; `settings`/`catalog`/`view`/`selection`/`ui` stay shared
 *  editor-chrome state. Analysis and treatment are copied on fork and
 *  restored on switch so a Design that already has simulations does not
 *  force a re-Compute until its document actually changes. */
export interface DesignRecord {
  id: string;
  label: string;
  parentId: string | null;
  doc: EditorDoc;
  history: EditorHistory;
  analysis: EditorState['analysis'];
  treatment: EditorState['treatment'];
  docRev: number;
  resultsRev: number | null;
  sortOrder: number;
  /** Job pipeline W3 (2026-09-06), §3.1: a terminal job result that arrived
   *  for THIS design but whose `origin.docHash` no longer matches the
   *  design's current doc — parked instead of applied. `null`/absent means
   *  nothing is parked. Cleared by applying a fresh, matching result for
   *  this design, or by `DISCARD_PENDING_RESULT`. Never persisted across a
   *  save/reload (session-only, like an in-flight job handle). */
  pendingResult?: PendingResult | null;
}

/** See `DesignRecord.pendingResult`. `payload` is the raw dispatch action
 *  that arrived too late to apply (`APPLY_ROOM_SOLVE` / `COMMIT_PLACEMENT_SIM`)
 *  — replaying it verbatim once the design catches up (or the user accepts
 *  it anyway) is a follow-up; today's UI (`PendingResultNotice`) only offers
 *  "Discard". */
export interface PendingResult {
  origin: JobOrigin;
  payload: unknown;
  receivedAt: number;
}

/** @deprecated Use `DesignRecord`. */
export type IterationDoc = DesignRecord;

export interface DesignListState {
  items: DesignRecord[];
  activeId: string;
}

/** @deprecated Use `DesignListState`. */
export type IterationState = DesignListState;

/** Client id for an unsaved room's first Design. Rebound to the backend
 *  numeric id after first save. Not undeletable — DELETE_ITERATION only
 *  refuses to remove the last remaining Design. */
export const ROOT_DESIGN_ID = 'root';
export const ROOT_ITERATION_ID = ROOT_DESIGN_ID;

export interface HistoryItem {
  doc: EditorDoc;
  /** Pre-edit `analysis` snapshot (via `cloneAnalysis`), pushed atomically
   *  with `doc` so UNDO restores geometry and results together in one step
   *  — never geometry alone with stale results left behind. Absent on older
   *  entries created before this field existed; UNDO/REDO fall back to just
   *  bumping `token` when it's missing. */
  analysis?: EditorState['analysis'];
}

export interface EditorHistory {
  past: HistoryItem[];
  future: HistoryItem[];
  coalesceKey: string | null;
  coalesceAt: number;
}

export interface TierResult {
  panels: PanelEntry[];
  revealIndices: RevealIndices;
  cost: number;
  rt60Before: Record<string, number>;
  rt60After: Record<string, number>;
  rt60Target: Record<string, number>;
  productsSummary: ProductSummary[];
}

/** The freq/rt60/soundstage trio `APPLY_ROOM_SOLVE` writes as current, up to
 *  twice: once for the candidate's own untreated ("bare") result, and again
 *  for the treated result when the evaluate stage succeeded. Same field
 *  shapes `COMMIT_PLACEMENT_SIM` already declares — reused rather than
 *  re-invented (AGENTS.md "Admin wire types"). */
export interface RoomSolveAnalysisTrio {
  freq: FreqResponseResponse;
  rt60: RT60Response | null;
  soundstage: SoundstageResponse | null;
}

export type EditorAction =
  | { type: 'SET_DIMS'; dims: Partial<RoomDims> }
  /** Atomic room-dimension edit — see the reducer case for why this exists
   *  instead of a SET_DIMS + SET_FOOTPRINT + N×SYNC_TRANSFORM sequence.
   *  `footprint` omitted means "leave it alone" (a plain box room); pass it
   *  whenever the caller also rescaled a custom footprint. `panels`/
   *  `furniture`/`speakers`/`listener` are the precomputed new transform for
   *  every item that moved with a wall/ceiling/corner/footprint change —
   *  omit an id (or `listener`) and it is left untouched. */
  | {
      type: 'RESIZE_ROOM';
      dims: Partial<RoomDims>;
      footprint?: Corner[] | null;
      panels?: { id: string; position: Vec3; rotation: Vec3 }[];
      furniture?: { id: string; position: Vec3; rotation?: Vec3; size?: PanelSize }[];
      speakers?: { id: string; position: Vec3 }[];
      listener?: { position: Vec3 };
    }
  | { type: 'SET_MATERIAL'; surface: 'floor' | 'wall'; key: string | null }
  | { type: 'SET_FURNISHING'; furnishing: Furnishing }
  | { type: 'SET_PIPELINE'; pipeline: ModalPipeline }
  | { type: 'SET_UNITS'; units: Units }
  | {
      type: 'ADD_SPEAKER'; subkind: SpeakerSubkind; position?: Vec3; rotationY?: number;
      // Optional — carried over when a new speaker is created FROM an
      // existing one (see useSpeakerPlacement's mirrorPair) rather than
      // freshly placed. Omitted by every other caller, so behaviour there
      // is unchanged.
      color?: string; freqResponse?: [number, number][] | null; faceListener?: boolean;
      /** The channel it plays, when the caller knows (the plan's Add names
       *  the first speakers in front of the seat the left and right mains).
       *  Omitted, a new speaker has none. */
      role?: SpeakerRole;
    }
  | {
      type: 'ADD_LISTENER'; position?: Vec3;
      /** Which way the seat faces; omitted, the room's forward. */
      rotationY?: number;
    }
  | { type: 'REMOVE_OBJECT'; id: string }
  | { type: 'DUPLICATE_SPEAKER'; id: string }
  | { type: 'SET_SPEAKER_ROLE'; id: string; role: SpeakerRole }
  | { type: 'SET_SPEAKER_CURVE'; id: string; curve: [number, number][] | null }
  /** A new type or size also moves the cabinet to where that type or size
   *  sits (the floor, for a tower or a sub) — see `reshapeSpeaker`. */
  | { type: 'UPDATE_SPEAKER'; id: string; patch: Partial<Pick<SpeakerEntry, 'subkind' | 'color' | 'faceListener' | 'size'>> }
  | { type: 'ADD_PANEL'; panel: Omit<PanelEntry, 'id'> }
  | { type: 'UPDATE_PANEL'; id: string; patch: Partial<Omit<PanelEntry, 'id'>> }
  | { type: 'REVEAL_PANELS'; panels: PanelEntry[] }
  | { type: 'ADD_FURNITURE'; furniture: Omit<FurnitureEntry, 'id'> }
  | { type: 'UPDATE_FURNITURE'; id: string; patch: Partial<Omit<FurnitureEntry, 'id'>> }
  | { type: 'SET_ROOM_SHAPE'; shape: RoomShape }
  | { type: 'SET_FOOTPRINT'; footprint: Corner[] | null }
  | { type: 'SET_WINGS'; wings: WingEntry[] }
  | { type: 'SET_CEILING'; ceiling: CeilingProfile | null }
  | { type: 'SET_NOGO_ZONES'; zones: NoGoZone[] }
  | { type: 'TRANSLATE_SCENE'; dx: number; dy: number }
  | { type: 'SELECT_SURFACE'; key: string | null }
  | { type: 'SET_SURFACE_COLOR'; key: string; color: string | null }
  | { type: 'SET_SURFACE_MATERIAL'; key: string; material: string | null }
  | { type: 'SET_FLOOR_FINISH'; finish: string | null }
  | { type: 'ADD_WINDOW'; window: Omit<WindowEntry, 'id'> }
  | { type: 'UPDATE_WINDOW'; id: string; patch: Partial<Omit<WindowEntry, 'id'>> }
  | { type: 'REMOVE_WINDOW'; id: string }
  | { type: 'ADD_DOOR'; door: Omit<DoorEntry, 'id'> }
  | { type: 'UPDATE_DOOR'; id: string; patch: Partial<Omit<DoorEntry, 'id'>> }
  | { type: 'REMOVE_DOOR'; id: string }
  | { type: 'ADD_MEDIA'; media: Omit<MediaEntry, 'id'> }
  | { type: 'UPDATE_MEDIA'; id: string; patch: Partial<Omit<MediaEntry, 'id'>> }
  /** Bind a Gear catalogue product to speakers (or unbind with `product: null`).
   *  The caller resolves the product: `size` is its body in metres and
   *  `subkind` the matching speaker kind. One undo step for all speakers. */
  | {
      type: 'SET_SPEAKER_PRODUCT';
      speakerIds: string[];
      product: { id: string; size: PanelSize; subkind: SpeakerSubkind } | null;
    }
  /** Create a whole channel group (front pair / center / surround pair /
   *  height pair / one sub) and bind it to a Gear catalogue product, in one
   *  undoable step — the Gear rail's "Add to room" for a group with no
   *  speakers yet (`@roomtreatment/room-flow`'s `planSpeakerGroupAdd` builds
   *  both fields; see its doc for why `setup` sometimes widens the declared
   *  layout). A no-op (returns `state` unchanged) when `speakers` is empty. */
  | { type: 'ADD_SPEAKERS'; speakers: SpeakerEntry[]; setup: SpeakerSetup }
  /** Place a receiver, processor or amplifier. `replaceId` swaps out an existing gear entry in the same undo step. */
  | { type: 'ADD_GEAR'; gear: Omit<GearEntry, 'id'>; replaceId?: string }
  /** A migration, not an edit: rooms saved while a TV, projector or screen
   *  model was gear get it moved into `media` (av-compat's
   *  `adoptGearDisplays` plans both lists; only the catalogue can tell a
   *  display from a receiver, so it runs once the catalogue has loaded).
   *  Replaces both lists whole. Not undoable: undoing a migration would only
   *  bring back the shape it exists to remove. */
  | { type: 'ADOPT_GEAR_DISPLAYS'; gear: GearEntry[]; media: MediaEntry[] }
  | { type: 'SET_CHECKLIST_ITEM'; itemId: string; done: boolean }
  /** User-set loudness target (`doc.listening.peakDb`), clamped to 70–115. */
  | { type: 'SET_LISTENING_LEVEL'; peakDb: number }
  /** The design's budget in whole dollars (`doc.budget.usd`), clamped to
   *  0-1,000,000; null clears it. */
  | { type: 'SET_BUDGET'; usd: number | null }
  /** One order line's where / price / promo / shipping (`doc.order.lines`):
   *  a field given as null goes back to its default, and a line left with
   *  nothing set is removed. Money is clamped to 0–1,000,000 and cents. */
  | { type: 'SET_ORDER_LINE'; key: string; patch: { [K in keyof OrderLine]?: OrderLine[K] | null } }
  /** The order's sales tax, percent, clamped to 0–25; null clears it. */
  | { type: 'SET_ORDER_TAX'; pct: number | null }
  | { type: 'SET_FACING'; yaw: number | null }
  | { type: 'SET_SPEAKER_SETUP'; setup: SpeakerSetup; speakers: SpeakerEntry[] }
  | { type: 'SELECT'; id: string | null }
  | { type: 'SYNC_TRANSFORM'; id: string; position: Vec3; rotationY?: number; rotation?: Vec3; preview?: boolean }
  | { type: 'SET_VIZ'; mode: VizMode }
  | { type: 'SET_RENDER_MODE'; mode: RenderMode }
  | { type: 'SET_REFLECTION_FILTER'; maxTimeMs?: number; order?: number | null }
  | { type: 'SET_MODAL_FREQ'; freq: number }
  | { type: 'SET_VELOCITY_LEVEL'; level: number }
  | { type: 'SET_MODAL_VIEW'; view: ModalView }
  | { type: 'SET_COMPARE'; compare: CompareMode }
  | { type: 'SET_VIZ_STATUS'; status: string }
  | { type: 'SET_DRAGGING'; dragging: boolean }
  | { type: 'SET_HOVER_FREQ'; freq: number | null }
  | { type: 'SET_PLACING_SKU'; sku: string | null }
  | { type: 'CATALOG_LOADING' }
  | { type: 'CATALOG_LOADED'; materials: MaterialsResponse; products: AdminProduct[] }
  | { type: 'CATALOG_ERROR'; error: string }
  | { type: 'ANALYSIS_START' }
  | {
      type: 'ANALYSIS_DONE';
      token: number;
      freq: FreqResponseResponse;
      rt60: RT60Response;
      soundstage: SoundstageResponse | null;
      durationMs: number;
      /** Job pipeline W2: `analyze_scene`'s four viz layers, written
       *  wholesale into `analysis.viz`. Omit (not `null`) for a caller that
       *  didn't run `analyze_scene` (there are none left in `web/` after
       *  this workstream, but older callers in tests may still omit it) —
       *  omitting leaves `analysis.viz` untouched rather than clearing it;
       *  pass `null` explicitly to clear it. */
      viz?: AnalysisViz | null;
    }
  | { type: 'ANALYSIS_ERROR'; token: number; error: string }
  /** Gallery accept: the chosen placement IS the current bare-room sim.
   *
   *  INVARIANT (2026-08-27, do not "simplify"):
   *  - Live gallery preview uses SYNC_TRANSFORM with `preview: true`,
   *    which IS sim-dirtying (so liveViz can refetch) but NOT undoable
   *    (flipping A/B/C is looking, not editing). A drag without `preview`
   *    stays one undo step. Accept must write the *accepted card's*
   *    FR/RT60/soundstage back as current or advanced chrome hides graphs +
   *    3D ("Scene changed — click Compute") even though the data is on
   *    the candidate. Template HYDRATE already sets token === scannedToken;
   *    live never did, which is why only the live path broke.
   *  - Do NOT add a `token` field (unlike ANALYSIS_DONE). Accept is sync
   *    with the already-moved scene; this catches `scannedToken` up to
   *    whatever `token` browsing advanced. A token mismatch would drop
   *    the write.
   *  - Do NOT add this to `isSimDirtying` or `isUndoable`. Dirtying would
   *    bump `token` past the `scannedToken` we just set. Undoable would
   *    push a history item for a chrome-adjacent write.
   *  - Graduation (`SET_EDITOR_MODE`, `SET_VIZ`) is chrome only and must
   *    also stay out of `isSimDirtying`.
   *  - Template 1-option Continue uses this same action so both paths
   *    graduate through one write-as-current.
   *  Gate: editorReducerContract "accepting a placement writes the sim
   *  as current"; reducerBehavior MUTATING_CASES COMMIT_PLACEMENT_SIM.
   *
   *  `origin` (job pipeline W3, §3.1, optional — omitted by every
   *  synchronous/local caller, e.g. the gallery accept path above): when
   *  present, `editorReducer` applies this result by ORIGIN, never by
   *  "whatever is active" — see the origin handling at the top of
   *  `editorReducer` below. A `designId` other than the active Design
   *  writes into that Design's own snapshot in `iterations.items`, never
   *  the live `doc`/`analysis`. A `docHash` that no longer matches the
   *  target Design's current doc parks the whole action as that Design's
   *  `pendingResult` instead of applying it. */
  | {
      type: 'COMMIT_PLACEMENT_SIM';
      freq: FreqResponseResponse;
      rt60: RT60Response | null;
      soundstage: SoundstageResponse | null;
      origin?: JobOrigin;
    }
  /** Unified `solve_room` result — placement + tier-'low' treatment +
   *  evaluate, applied as ONE reducer step (one history entry) instead of
   *  guided onboarding's old three separate dispatches: a SYNC_TRANSFORM per
   *  agent (moving the listener, then each speaker), then
   *  COMMIT_PLACEMENT_SIM, then a separate TREATMENT_TIER_DONE once panels
   *  were solved. See `applyRoomSolve` for the exact order of effect:
   *   1. move the listener + every speaker to `candidate`'s solved pose —
   *      MUST match `applyOptimizeCandidate`
   *      (packages/room-flow/src/placement.ts:89-112) exactly for the same
   *      candidate; pinned by editorReducerContract's anti-drift test;
   *   2. write `bare` as current (COMMIT_PLACEMENT_SIM semantics above) —
   *      the before side of the comparison;
   *   3. apply `treatment` for tier 'low' (hardcoded — the only tier a
   *      solve_room job returns) with TREATMENT_TIER_DONE's coreReducer
   *      semantics: store under `treatment.results.low` and
   *      `applyTierSliceToDoc` the panels into `doc.panels`. `null` means
   *      the treatment stage failed — skipped entirely, seat/speakers still
   *      moved. Deliberately NOT the editorReducer wrapper's
   *      `applyPlacementSim` shortcut used elsewhere for TREATMENT_TIER_DONE
   *      (bottom of this file): that shortcut unconditionally re-syncs
   *      `scannedToken`, which would mark a failed evaluate stage (next
   *      point) as fresh — the exact half-updated frame this action exists
   *      to prevent;
   *   4. when `analysis` (the TREATED trio) is non-null, write it as current
   *      too, so `scannedToken === token` and `analysisMatchesScene` is true
   *      on landing. `null` means the evaluate stage failed — leave the
   *      analysis stale; the caller dispatches BUMP_ANALYSIS separately so
   *      the editor's normal auto-sim fills the charts instead of the room
   *      claiming a result nobody computed.
   *  A normal `coreReducer` case (`applyRoomSolve`) — but it computes its
   *  OWN final `analysis` (token bump(s) + writeCurrentAnalysis) rather than
   *  leaving that to the generic wrapper, so it is deliberately excluded
   *  from `isSimDirtying`/`staleClassFor` (both default to false/'none' for
   *  it, same as `COMMIT_PLACEMENT_SIM`) and added to `isUndoable` instead,
   *  so `editorReducer`'s generic history push still fires exactly once per
   *  dispatch, using the pre-edit `state` it already has. Gate:
   *  editorReducerContract.test.ts. */
  | {
      type: 'APPLY_ROOM_SOLVE';
      candidate: OptimizeCandidate;
      bare: RoomSolveAnalysisTrio;
      treatment: TierResult | null;
      analysis: RoomSolveAnalysisTrio | null;
      /** See `COMMIT_PLACEMENT_SIM.origin`'s doc comment — same apply-by-
       *  origin contract applies here. */
      origin?: JobOrigin;
    }
  | { type: 'BUMP_ANALYSIS' }
  | { type: 'ARM_VOXEL'; signature: string }
  | { type: 'TREATMENT_TIER_START'; tier: TreatmentTier }
  | { type: 'TREATMENT_TIER_DONE'; tier: TreatmentTier; result: TierResult }
  /** Job pipeline W3 (2026-09-06), §3.1: drop a parked result (see
   *  `DesignRecord.pendingResult`) the user chose not to apply. No-op if
   *  the design is gone or nothing is parked on it. */
  | { type: 'DISCARD_PENDING_RESULT'; designId: string }
  | { type: 'TREATMENT_TIER_ERROR'; tier: TreatmentTier; error: string }
  /** The user stopped this tier's solve. Deliberately not TREATMENT_TIER_ERROR
   *  with a message: stopping is a choice the user made, and routing it through
   *  the error channel paints a red block reporting their own button press back
   *  at them. It also has to CLEAR any previous error — the tier is now simply
   *  idle, and a stale failure from an earlier attempt would outlive the run
   *  that replaced it. */
  | { type: 'TREATMENT_TIER_CANCELLED'; tier: TreatmentTier }
  | { type: 'SET_ACTIVE_TIER'; tier: TreatmentTier }
  | { type: 'SET_ACTIVE_REVEAL'; level: RevealLevel }
  | { type: 'PRODUCTS_UPDATED'; products: AdminProduct[] }
  | { type: 'OPEN_DIALOG'; dialog: EditorDialog }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SET_EDITOR_MODE'; mode: 'guided' | 'advanced' }
  | { type: 'SET_GUIDED_STEP'; step: string }
  /** Clone `fromId`'s CURRENT doc (its live edits if it's the active
   *  iteration, its last-synced snapshot otherwise) into a new iteration,
   *  copying analysis/treatment so simulations travel with the Design,
   *  fresh undo history, and make it active. `label` defaults to
   *  copyLabel(source) (filesystem-style uniquify). Colliding explicit
   *  labels are uniquified too. No-op if `fromId` doesn't exist. */
  | { type: 'FORK_ITERATION'; fromId: string; label?: string }
  /** A fresh guided document (createBlankDoc), empty analysis, parentId
   *  null. Label defaults to nextBlankLabel. Becomes active. */
  | { type: 'ADD_BLANK_ITERATION'; label?: string }
  /** Snapshot the outgoing iteration's live `doc`/`history`/`analysis`/
   *  `treatment` back into `iterations.items`, then make `id` the active
   *  iteration (loading ITS stored doc/history/results). No-op if `id` is
   *  already active or doesn't exist. */
  | { type: 'SWITCH_ITERATION'; id: string }
  /** Removes an iteration. No-op when it is the last remaining Design.
   *  Any children re-parent to the deleted node's own parent. Deleting the
   *  active iteration falls back to the remaining item with the lowest
   *  sortOrder. */
  | { type: 'DELETE_ITERATION'; id: string }
  /** Moves `id` to `toIndex` in `iterations.items` (clamped to the array's
   *  bounds). Display order only — doesn't touch `doc`/`history`/`activeId`,
   *  unlike FORK/SWITCH/DELETE which all snapshot the live doc because they
   *  change what's active. No-op if `id` doesn't exist or is already there. */
  | { type: 'REORDER_ITERATION'; id: string; toIndex: number }
  | { type: 'RENAME_ITERATION'; id: string; label: string }
  /** Remap a client-only id (e.g. `'root'`) to the backend Design id after
   *  the first save. Updates items[].id, parentId, and activeId. */
  | { type: 'REBIND_ITERATION'; localId: string; backendId: string }
  | { type: 'SYNC_DESIGN_REVISIONS'; id: string; docRev: number; resultsRev: number | null }
  /** A LiDAR scan finished: its geometry replaces the room shell of the
   *  active Design (`applyScannedGeometry`). One undo step, and unlike
   *  HYDRATE it keeps the Design list, history and the user's answers. */
  | { type: 'APPLY_SCAN'; scanned: EditorDoc }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'HYDRATE'; doc: EditorDoc; results?: Record<string, unknown>; iterations?: DesignListState; treatment?: EditorState['treatment'] };

/** Actions `coreReducer` handles. UNDO/REDO stay in `editorReducer` only. */
export type CoreAction = Exclude<EditorAction, { type: 'UNDO' } | { type: 'REDO' }>;

export function isSimDirtying(action: EditorAction): boolean {
  switch (action.type) {
    case 'SET_DIMS':
    case 'RESIZE_ROOM':
    case 'SET_MATERIAL':
    case 'SET_FURNISHING':
    case 'SET_PIPELINE':
    case 'ADD_SPEAKER':
    case 'ADD_LISTENER':
    case 'REMOVE_OBJECT':
    case 'DUPLICATE_SPEAKER':
    case 'SET_SPEAKER_ROLE':
    case 'SET_SPEAKER_CURVE':
    case 'UPDATE_SPEAKER':
    case 'ADD_PANEL':
    case 'UPDATE_PANEL':
    case 'REVEAL_PANELS':
    case 'ADD_FURNITURE':
    case 'UPDATE_FURNITURE':
    case 'ADD_WINDOW':
    case 'UPDATE_WINDOW':
    case 'REMOVE_WINDOW':
    case 'ADD_DOOR':
    case 'UPDATE_DOOR':
    case 'REMOVE_DOOR':
    case 'SET_SURFACE_MATERIAL':
    // A finish carries its own acoustic material, so it moves the solve for
    // the same reason a material change does — never a purely visual edit.
    case 'SET_FLOOR_FINISH':
    case 'SET_ROOM_SHAPE':
    case 'SET_FOOTPRINT':
    case 'SET_WINGS':
    case 'SET_CEILING':
    case 'SET_NOGO_ZONES':
    case 'TRANSLATE_SCENE':
    case 'SET_FACING':
    case 'SET_SPEAKER_SETUP':
    case 'ADD_MEDIA':
    case 'UPDATE_MEDIA':
    // A display that was gear was invisible to the solver; as media it is a
    // reflector, so the last solve no longer describes the room.
    case 'ADOPT_GEAR_DISPLAYS':
    // Binding a product can change a speaker's subkind, which moves its
    // low-frequency gate in the solver.
    case 'SET_SPEAKER_PRODUCT':
    case 'ADD_SPEAKERS':
    case 'SYNC_TRANSFORM':
    case 'TREATMENT_TIER_DONE':
    case 'SET_ACTIVE_TIER':
    case 'SET_ACTIVE_REVEAL':
    case 'APPLY_SCAN':
      return true;
    // COMMIT_PLACEMENT_SIM, SET_VIZ, SET_EDITOR_MODE, SET_GUIDED_STEP, HYDRATE,
    // ANALYSIS_DONE, ARM_VOXEL — stay in this default. Gallery accept writes
    // the chosen placement as the current bare-room sim; if it dirtied the
    // token, advanced chrome would show "Scene changed — click Compute" for
    // a room the user just accepted. Gate: editorReducerContract
    // "COMMIT_PLACEMENT_SIM is not sim-dirtying".
    // APPLY_ROOM_SOLVE joins this default for the same reason: its
    // coreReducer case (`applyRoomSolve`) already bumps `analysis.token`
    // itself (once, or twice when a treatment result also lands), so
    // dirtying it here would bump a second time past the token it just
    // wrote as current.
    default:
      return false;
  }
}

/** Which before/after result slot(s) an edit invalidates. `'panel'` — only
 *  the treated room changed (ADD_PANEL/UPDATE_PANEL/REVEAL_PANELS, or a
 *  REMOVE_OBJECT/SYNC_TRANSFORM that targets a panel — resolved by looking
 *  the id up in `doc`, since those two actions also move speakers/furniture/
 *  the listener). `'other'` — anything about the room/furniture/speakers
 *  itself changed, which invalidates the untreated baseline too. `'none'` —
 *  everything else (view-only actions, tier/reveal switches on
 *  already-computed wizard results, or a completion action, none of which
 *  represent an edit the user made). */
export type StaleClass = 'panel' | 'other' | 'none';

export function staleClassFor(action: EditorAction, doc: EditorDoc): StaleClass {
  switch (action.type) {
    case 'ADD_PANEL':
    case 'UPDATE_PANEL':
    case 'REVEAL_PANELS':
      return 'panel';
    case 'REMOVE_OBJECT':
    case 'SYNC_TRANSFORM': {
      const kind = objectKindOf(doc, action.id);
      // Gear electronics never reach the solver.
      if (kind === 'gear') return 'none';
      return kind === 'panel' ? 'panel' : 'other';
    }
    case 'SET_DIMS':
    case 'RESIZE_ROOM':
    case 'SET_MATERIAL':
    case 'SET_FURNISHING':
    case 'SET_PIPELINE':
    case 'ADD_SPEAKER':
    case 'ADD_LISTENER':
    case 'DUPLICATE_SPEAKER':
    case 'SET_SPEAKER_ROLE':
    case 'SET_SPEAKER_CURVE':
    case 'UPDATE_SPEAKER':
    case 'ADD_FURNITURE':
    case 'UPDATE_FURNITURE':
    case 'ADD_WINDOW':
    case 'UPDATE_WINDOW':
    case 'REMOVE_WINDOW':
    case 'ADD_DOOR':
    case 'UPDATE_DOOR':
    case 'REMOVE_DOOR':
    case 'SET_SURFACE_MATERIAL':
    // A finish carries its own acoustic material, so it moves the solve for
    // the same reason a material change does — never a purely visual edit.
    case 'SET_FLOOR_FINISH':
    case 'SET_ROOM_SHAPE':
    case 'SET_FOOTPRINT':
    case 'SET_WINGS':
    case 'SET_CEILING':
    case 'SET_NOGO_ZONES':
    case 'TRANSLATE_SCENE':
    case 'SET_FACING':
    case 'SET_SPEAKER_SETUP':
    // A TV or projection screen is a hard reflector the size of a treatment
    // panel, and it blocks wall cells the solver would otherwise use — so it
    // changes the untreated baseline too, not just the treated result.
    case 'ADD_MEDIA':
    case 'UPDATE_MEDIA':
    case 'ADOPT_GEAR_DISPLAYS':
    case 'SET_SPEAKER_PRODUCT':
    case 'ADD_SPEAKERS':
    case 'APPLY_SCAN':
      return 'other';
    default:
      return 'none';
  }
}

/** Apply one action's staleness fallout to `analysis`: mark the invalidated
 *  slot(s) stale, and null the combined freq/rt60/soundstage result so noone
 *  ever reads a result that no longer matches the room it's displayed next
 *  to. (Freq/RT60/soundstage carry the untreated baseline and the treated
 *  result in ONE fused response — `mag`/`mag_baseline`, `rt60`/
 *  `rt60_baseline` — there is no independently-addressable "before" object
 *  to leave in place, so a panel-only edit still nulls it; the very next
 *  Compute repopulates both halves in the same call that already has to
 *  happen for the treated side.) */
export function applyStaleClass(
  analysis: EditorState['analysis'],
  cls: StaleClass,
): EditorState['analysis'] {
  if (cls === 'none') return analysis;
  return {
    ...analysis,
    freq: null,
    rt60: null,
    soundstage: null,
    afterStale: true,
    beforeStale: cls === 'other' ? true : analysis.beforeStale,
  };
}

const HISTORY_LIMIT = 100;
const COALESCE_MS = 500;
const EMPTY_HISTORY: EditorHistory = { past: [], future: [], coalesceKey: null, coalesceAt: 0 };

/** Reused by both `createInitialState` and iteration switching — a fresh
 *  iteration (or one just switched into) has never been scanned. */
const EMPTY_ANALYSIS: EditorState['analysis'] = {
  freq: null, rt60: null, soundstage: null, loading: false, error: null,
  token: 0, scanNonce: 0, scannedToken: -1, voxelArmedSig: null, lastSolveMs: null,
  // Never simulated yet — both sides genuinely need a first Compute.
  beforeStale: true, afterStale: true, afterResultVersion: 0, viz: null,
};
const EMPTY_TREATMENT: EditorState['treatment'] = {
  running: { low: false, medium: false, high: false },
  results: { low: null, medium: null, high: null },
  activeTier: 'medium',
  activeReveal: 'full',
  error: null,
};

function cloneDoc(doc: EditorDoc): EditorDoc {
  return structuredClone(doc);
}

function cloneAnalysis(analysis: EditorState['analysis']): EditorState['analysis'] {
  return {
    ...analysis,
    freq: analysis.freq ? structuredClone(analysis.freq) : null,
    rt60: analysis.rt60 ? structuredClone(analysis.rt60) : null,
    soundstage: analysis.soundstage ? structuredClone(analysis.soundstage) : null,
    viz: analysis.viz ? structuredClone(analysis.viz) : null,
  };
}

function cloneTreatment(treatment: EditorState['treatment']): EditorState['treatment'] {
  return {
    ...treatment,
    running: { ...treatment.running },
    results: { ...treatment.results },
  };
}

function cloneHistory(history: EditorHistory): EditorHistory {
  return {
    past: [...history.past],
    future: [...history.future],
    coalesceKey: history.coalesceKey,
    coalesceAt: history.coalesceAt,
  };
}

export function makeIterationDoc(input: {
  id: string;
  label: string;
  parentId?: string | null;
  doc: EditorDoc;
  history?: EditorHistory;
  analysis?: EditorState['analysis'];
  treatment?: EditorState['treatment'];
  docRev?: number;
  resultsRev?: number | null;
  sortOrder?: number;
}): DesignRecord {
  return {
    id: input.id,
    label: input.label,
    parentId: input.parentId ?? null,
    doc: cloneDoc(normalizeEditorDoc(input.doc)),
    history: input.history ?? EMPTY_HISTORY,
    analysis: input.analysis ? cloneAnalysis(input.analysis) : cloneAnalysis(EMPTY_ANALYSIS),
    treatment: input.treatment ? cloneTreatment(input.treatment) : cloneTreatment(EMPTY_TREATMENT),
    docRev: input.docRev ?? 0,
    resultsRev: input.resultsRev ?? null,
    sortOrder: input.sortOrder ?? 0,
  };
}

export const makeDesignRecord = makeIterationDoc;

function snapshotActive(state: EditorState): DesignRecord[] {
  return state.iterations.items.map((it) =>
    it.id === state.iterations.activeId
      ? {
          ...it,
          doc: cloneDoc(state.doc),
          history: cloneHistory(state.history),
          analysis: cloneAnalysis(state.analysis),
          treatment: cloneTreatment(state.treatment),
        }
      : it,
  );
}

/** Snapshot live doc/analysis/treatment onto the active item so a draft or
 *  hydrate payload has the in-memory edits, not the last switch-away stash. */
export function snapshotActiveIterations(state: EditorState): DesignListState {
  return { items: snapshotActive(state), activeId: state.iterations.activeId };
}

/** Load a Design from `items` into live editor state. Session chrome
 *  (`settings`, `catalog`, `view`, `ui`) is preserved from `state`;
 *  per-Design fields (`doc`, `history`, `analysis`, `treatment`) are
 *  cloned from the record. Selection is cleared. */
export function activateDesign(
  state: EditorState,
  items: DesignRecord[],
  id: string,
): EditorState {
  const loaded = items.find((it) => it.id === id);
  if (!loaded) return state;
  return {
    ...state,
    doc: cloneDoc(normalizeEditorDoc(loaded.doc)),
    history: cloneHistory(loaded.history),
    analysis: cloneAnalysis(loaded.analysis),
    treatment: cloneTreatment(loaded.treatment),
    selection: null,
    iterations: { items, activeId: id },
  };
}

/** Restore `analysis.viz` from a Design's `viz_json` (v1 or v2 — see
 *  `persistedViz.ts`). Exported separately from `analysisFromResults` so a
 *  caller that already has `results` restored (or none at all) can still
 *  seed viz on its own. */
export function analysisVizFromPersisted(viz?: unknown): AnalysisViz | null {
  const fields = vizFromPersisted(viz);
  if (!fields) return null;
  return {
    modal: fields.modal as AnalysisViz['modal'],
    volume: fields.volume as AnalysisViz['volume'],
    velocity: fields.velocity as AnalysisViz['velocity'],
    raytrace: fields.raytrace as AnalysisViz['raytrace'],
  };
}

export function analysisFromResults(
  results?: Record<string, unknown>,
  viz?: unknown,
): EditorState['analysis'] {
  const base = cloneAnalysis(EMPTY_ANALYSIS);
  const restoredViz = analysisVizFromPersisted(viz);
  if (!results) return { ...base, viz: restoredViz };
  const freq = (results.freq as EditorState['analysis']['freq']) ?? null;
  const rt60 = (results.rt60 as EditorState['analysis']['rt60']) ?? null;
  const soundstage = (results.soundstage as EditorState['analysis']['soundstage']) ?? null;
  if (!freq && !rt60 && !soundstage) return { ...base, viz: restoredViz };
  // Older saved rooms never recorded these flags — a persisted result with
  // no flag on it is trusted as fresh (matches this room's pre-flag
  // behaviour) rather than treated as stale by default.
  const beforeStale = typeof results.beforeStale === 'boolean' ? results.beforeStale : false;
  const afterStale = typeof results.afterStale === 'boolean' ? results.afterStale : false;
  return {
    ...base, freq, rt60, soundstage, scannedToken: base.token, beforeStale, afterStale,
    viz: restoredViz,
  };
}

export function isUndoable(action: EditorAction): boolean {
  switch (action.type) {
    case 'SET_DIMS':
    case 'RESIZE_ROOM':
    case 'SET_MATERIAL':
    case 'SET_FURNISHING':
    case 'ADD_SPEAKER':
    case 'ADD_LISTENER':
    case 'REMOVE_OBJECT':
    case 'DUPLICATE_SPEAKER':
    case 'SET_SPEAKER_ROLE':
    case 'SET_SPEAKER_CURVE':
    case 'UPDATE_SPEAKER':
    case 'ADD_PANEL':
    case 'UPDATE_PANEL':
    case 'ADD_FURNITURE':
    case 'UPDATE_FURNITURE':
    case 'SET_ROOM_SHAPE':
    case 'SET_FOOTPRINT':
    case 'SET_WINGS':
    case 'SET_CEILING':
    case 'SET_NOGO_ZONES':
    case 'TRANSLATE_SCENE':
    case 'SET_FACING':
    case 'SET_SPEAKER_SETUP':
    case 'SET_SURFACE_COLOR':
    case 'SET_SURFACE_MATERIAL':
    case 'SET_FLOOR_FINISH':
    case 'ADD_WINDOW':
    case 'UPDATE_WINDOW':
    case 'REMOVE_WINDOW':
    case 'ADD_DOOR':
    case 'UPDATE_DOOR':
    case 'REMOVE_DOOR':
    case 'ADD_MEDIA':
    case 'UPDATE_MEDIA':
    case 'SET_SPEAKER_PRODUCT':
    case 'ADD_SPEAKERS':
    case 'ADD_GEAR':
    // A tick lives in the doc, so it must be a history step: otherwise undoing
    // a later edit would restore the pre-tick doc and silently untick it.
    case 'SET_CHECKLIST_ITEM':
    // Same reasoning as the tick above: the loudness target lives in the doc.
    case 'SET_LISTENING_LEVEL':
    // And so does the budget, and the order.
    case 'SET_BUDGET':
    case 'SET_ORDER_LINE':
    case 'SET_ORDER_TAX':
    // The whole solve — seat, speakers, panels, analysis — is one undo step.
    // `applyRoomSolve` (coreReducer) already computes its own final
    // `analysis`/token internally, so this must stay OUT of `isSimDirtying`
    // and `staleClassFor` (both default to false/'none' for it, same as
    // COMMIT_PLACEMENT_SIM) — otherwise the generic wrapper below would bump
    // token or null the just-written result a second time.
    case 'APPLY_ROOM_SOLVE':
    case 'APPLY_SCAN':
      return true;
    case 'SYNC_TRANSFORM':
      // Gallery browsing (`preview`) must not create undo steps. A real
      // drag or numeric edit omits the flag and stays undoable.
      return action.preview !== true;
    default:
      return false;
  }
}

function coalesceKeyFor(action: EditorAction): string | null {
  switch (action.type) {
    // Per-id, matching UPDATE_PANEL / UPDATE_FURNITURE. A global `'xf'`
    // merged every in-flight drag into one undo step, so Ctrl+Z after
    // moving two objects restored the pre-both sim as if nothing had
    // changed. One object's drag still coalesces with itself.
    case 'SYNC_TRANSFORM': return `xf:${action.id}`;
    case 'SET_DIMS':
    case 'RESIZE_ROOM':
    case 'SET_FOOTPRINT':
    case 'SET_WINGS': return 'room';
    case 'SET_CEILING': return 'ceiling';
    case 'SET_NOGO_ZONES': return 'nogo';
    case 'TRANSLATE_SCENE': return 'room';
    case 'SET_FACING': return 'facing';
    case 'SET_SPEAKER_CURVE': return `curve:${action.id}`;
    case 'UPDATE_SPEAKER': return `speaker:${action.id}`;
    case 'UPDATE_PANEL': return `panel:${action.id}`;
    case 'UPDATE_FURNITURE': return `furniture:${action.id}`;
    case 'SET_SURFACE_COLOR': return `surface:${action.key}`;
    case 'SET_SURFACE_MATERIAL': return `surfaceMat:${action.key}`;
    // Flicking through the picker is one decision, not one undo step each.
    case 'SET_FLOOR_FINISH': return 'floorFinish';
    case 'UPDATE_WINDOW': return `window:${action.id}`;
    case 'UPDATE_DOOR': return `door:${action.id}`;
    case 'UPDATE_MEDIA': return `media:${action.id}`;
    default: return null;
  }
}

function genId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * Which collection an editor id belongs to, or null if it belongs to none.
 *
 * The document is the only authority on what an object IS. Ids are opaque
 * uuids that carry no type information, so any code that needs the kind must
 * look it up here rather than infer it — and the one place that did not (the
 * SELECT reducer, which asserted 'speaker' for everything) broke every
 * kind-gated inspector in the editor.
 *
 * Exported so callers outside the reducer can ask the same question the same
 * way, and so `editorState.test.ts` can pin it against `ObjectKind` directly.
 */
export function objectKindOf(doc: EditorDoc, id: string): ObjectKind | null {
  if (doc.listener?.id === id) return 'listener';
  if (doc.speakers.some((s) => s.id === id)) return 'speaker';
  if (doc.panels.some((p) => p.id === id)) return 'panel';
  if (doc.furniture.some((f) => f.id === id)) return 'furniture';
  if ((doc.windows ?? []).some((w) => w.id === id)) return 'window';
  if ((doc.doors ?? []).some((d) => d.id === id)) return 'door';
  if ((doc.media ?? []).some((m) => m.id === id)) return 'media';
  if ((doc.gear ?? []).some((g) => g.id === id)) return 'gear';
  return null;
}

export function defaultSpeakerY(
  subkind: SpeakerSubkind,
  roomH: number,
  listenerY?: number | null,
): number {
  const heights: Record<SpeakerSubkind, number> = {
    subwoofer: 0.40,
    floorstanding: 1.05,
    bookshelf: 0.35,
    nearfield: 0.30,
    overhead: 0.20,
  };
  const cabinetH = heights[subkind] ?? 0.35;
  switch (subkind) {
    case 'subwoofer':
    case 'floorstanding':
      return cabinetH / 2;
    case 'bookshelf':
      if (listenerY != null && Number.isFinite(listenerY)) {
        return Math.max(cabinetH / 2, listenerY + 0.03 - cabinetH / 2);
      }
      return 1.10;
    case 'nearfield':
      return 1.10;
    case 'overhead':
      return Math.max(0, roomH - cabinetH / 2);
  }
}

function defaultSpeakerPos(
  dims: RoomDims,
  subkind: SpeakerSubkind,
  listenerY?: number | null,
): Vec3 {
  return [dims.Lx / 2, defaultSpeakerY(subkind, dims.H, listenerY), dims.Ly * 0.22];
}

export function createBlankDoc(): EditorDoc {
  return {
    dims: { Lx: 7.0, Ly: 5.0, H: 3.0 },
    floorMaterial: null,
    wallMaterial: null,
    furnishing: 'moderate',
    speakers: [],
    listener: null,
    panels: [],
    furniture: [],
    media: [],
    roomShape: 'rectangular',
    footprint: null,
    wings: [],
    ceiling: null,
    noGoZones: [],
    facingYawRad: null,
    speakerSetup: {
      layout: 'stereo', sub: 0, mainType: 'bookshelf',
      headFreq: { low: 50, high: 20000 },
      subFreq: { low: 20, high: 100 },
      overheadFreq: { low: 100, high: 20000 },
    },
    editorMode: 'guided',
    source: 'manual',
  };
}

/** Merge a wire/partial doc onto `createBlankDoc()` defaults. Saved rooms and
 *  API payloads often omit newer top-level fields (`wings`, `furniture`, …);
 *  without this, a shallow spread leaves them `undefined` and runtime code
 *  that assumes arrays (SceneCanvas camera framing, etc.) throws. */
export function normalizeEditorDoc(partial: Partial<EditorDoc>): EditorDoc {
  const base = createBlankDoc();
  const merged: EditorDoc = {
    ...base,
    ...partial,
    dims: { ...base.dims, ...(partial.dims ?? {}) },
    speakerSetup: partial.speakerSetup
      ? { ...base.speakerSetup, ...partial.speakerSetup }
      : base.speakerSetup,
    wings: partial.wings ?? base.wings,
    panels: partial.panels ?? base.panels,
    speakers: partial.speakers ?? base.speakers,
    furniture: partial.furniture ?? base.furniture,
    noGoZones: partial.noGoZones ?? base.noGoZones,
    footprint: partial.footprint !== undefined ? partial.footprint : base.footprint,
    ceiling: partial.ceiling !== undefined ? partial.ceiling : base.ceiling,
    floorMaterial: partial.floorMaterial !== undefined ? partial.floorMaterial : base.floorMaterial,
    wallMaterial: partial.wallMaterial !== undefined ? partial.wallMaterial : base.wallMaterial,
    surfaceMaterials: partial.surfaceMaterials ?? base.surfaceMaterials,
    floorFinish: partial.floorFinish !== undefined ? partial.floorFinish : base.floorFinish,
    windows: partial.windows ?? base.windows,
    doors: partial.doors ?? base.doors,
    media: partial.media ?? base.media,
    listener: partial.listener !== undefined ? partial.listener : base.listener,
  };
  return reseatCornerTraps(merged);
}

export function createInitialState(): EditorState {
  const doc = createBlankDoc();
  return {
    doc,
    settings: { pipeline: 'voxel', units: 'metric' },
    catalog: { materials: null, products: null, loading: false, error: null },
    view: {
      vizMode: 'none', renderMode: 'design', modalFreq: 40, velocityLevel: 1, modalView: 'topdown',
      vizStatus: '', dragging: false,
      hoverFreq: null,
      placingSku: null,
      selectedSurface: null,
      // The imaging-critical window, not the whole tail.
      //
      // This used to open at 150 — the slider's own maximum — so entering
      // Reflections showed every arrival out to 150 ms at once. The early
      // reflections a user can actually act on (a panel at a mirror point)
      // arrive inside the first ~20 ms and were buried under a room's worth
      // of late diffuse energy that no panel placement changes.
      //
      // 25 ms keeps the imaging band with margin for a large room's direct
      // path. The slider still reaches 150 for anyone chasing colouration or
      // slap echo; this only decides what the first look shows.
      reflectionMaxTimeMs: 25,
      reflectionOrder: null,
      compare: 'after',
    },
    selection: null,
    analysis: EMPTY_ANALYSIS,
    treatment: EMPTY_TREATMENT,
    ui: { dialog: null },
    history: EMPTY_HISTORY,
    iterations: {
      items: [makeIterationDoc({
        id: ROOT_ITERATION_ID,
        label: DESIGN_COPY.defaultLabel,
        parentId: null,
        doc,
        history: EMPTY_HISTORY,
        sortOrder: 0,
      })],
      activeId: ROOT_ITERATION_ID,
    },
  };
}

function applyTierSliceToDoc(state: EditorState): EditorState {
  const { activeTier, activeReveal, results } = state.treatment;
  const r = results[activeTier];
  if (!r) return state;
  const idx = r.revealIndices?.[activeReveal] ?? r.panels.length;
  const sliced = r.panels.slice(0, idx);
  return { ...state, doc: { ...state.doc, panels: sliced } };
}

function vec3Eq(a: [number, number, number], b: [number, number, number]): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function rot3Eq(
  a: [number, number, number] | undefined,
  b: [number, number, number] | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return vec3Eq(a, b);
}

/** Move the listener to `position`/`rotationY` — SYNC_TRANSFORM's listener
 *  transform, factored out so `applyRoomSolve` (APPLY_ROOM_SOLVE) can move
 *  the seat the same way without re-deriving the yaw fallback. Omitted
 *  `rotationY` keeps the listener's current yaw, same as SYNC_TRANSFORM's
 *  default. */
function moveListenerTo(listener: ListenerEntry, position: Vec3, rotationY?: number): ListenerEntry {
  return { ...listener, position, rotationY: rotationY ?? listener.rotationY };
}

/** Move a speaker to `position`, re-aiming it at `listener` unless the user
 *  has frozen this speaker's yaw (`faceListener === false`) — SYNC_TRANSFORM's
 *  speaker transform, factored out so `applyRoomSolve` can move every
 *  channel the same way without re-deriving the lock check.
 *
 *  A floor-standing cabinet only takes X and Z from `position`: its bottom
 *  stays on the floor (`seatOnFloor`), whatever height a drag, a typed value
 *  or a solver candidate asked for. The placement solver puts every
 *  head-level channel at the seat's ear height, towers included, so this is
 *  also what keeps a solved tower standing. */
function moveSpeakerTo(
  speaker: SpeakerEntry,
  position: Vec3,
  listener: ListenerEntry | null,
  rotationY?: number,
): SpeakerEntry {
  const moved = seatOnFloor({ ...speaker, position });
  const locked = speaker.faceListener !== false && listener != null;
  return {
    ...moved,
    rotationY: locked ? faceToward(moved.position, listener!.position) : (rotationY ?? speaker.rotationY),
  };
}

/**
 * UPDATE_SPEAKER's patch applied to `speaker`, with the cabinet re-seated when
 * its type or size changed:
 *  - a floor-standing type stands on the floor, always (`seatOnFloor`);
 *  - any other NEW type moves to that type's natural height
 *    (`defaultSpeakerY`), so a tower switched to a bookshelf goes up to the
 *    ear rather than hovering at the tower's old centre;
 *  - a new SIZE of the same type keeps the cabinet's bottom where it was
 *    (it stands on a stand or a shelf), clamped so its top stays under the
 *    ceiling;
 *  - anything else (colour, the facing lock) leaves the position alone.
 */
function reshapeSpeaker(
  speaker: SpeakerEntry,
  patch: Extract<EditorAction, { type: 'UPDATE_SPEAKER' }>['patch'],
  doc: EditorDoc,
): SpeakerEntry {
  const next: SpeakerEntry = { ...speaker, ...patch };
  if (standsOnFloor(next.subkind)) return seatOnFloor(next);
  const [x, , z] = speaker.position;
  if (next.subkind !== speaker.subkind) {
    const y = defaultSpeakerY(next.subkind, doc.dims.H, doc.listener?.position[1]);
    return { ...next, position: [x, y, z] };
  }
  const fromH = speakerCabinetH(speaker);
  const toH = speakerCabinetH(next);
  if (toH === fromH) return next;
  const bottom = clampBottomHeight(bottomHeightFromCenterY(speaker.position[1], fromH), toH, doc.dims.H);
  return { ...next, position: [x, centerYFromBottomHeight(bottom, toH), z] };
}

/**
 * `speaker` bound to a catalogue product — or, with `null`, back to its
 * subkind's generic box — with its cabinet re-seated: a floor-standing type
 * on the floor (`seatOnFloor`); a speaker whose TYPE the product changed, at
 * that type's natural height (a tower bound to a bookshelf model goes up to
 * the ear rather than keeping the tower's centre); otherwise where it stood.
 * SET_SPEAKER_PRODUCT and the Gear panel's preview (web productImpact.ts) both
 * call this, so what Add previews is what it does.
 */
export function bindSpeakerProduct(
  doc: Pick<EditorDoc, 'dims' | 'listener'>,
  speaker: SpeakerEntry,
  product: { id: string; size: PanelSize; subkind: SpeakerSubkind } | null,
): SpeakerEntry {
  if (!product) {
    // Unbinding returns the cabinet to its subkind's default box.
    const { productId: _p, size: _s, ...rest } = speaker;
    return seatOnFloor(rest);
  }
  const next: SpeakerEntry = { ...speaker, productId: product.id, size: product.size, subkind: product.subkind };
  if (standsOnFloor(next.subkind)) return seatOnFloor(next);
  if (next.subkind === speaker.subkind) return next;
  const [x, , z] = speaker.position;
  return { ...next, position: [x, defaultSpeakerY(next.subkind, doc.dims.H, doc.listener?.position[1]), z] };
}

/** Mark the live analysis as matching the current doc. ONE write-as-current
 *  path for Compute (`ANALYSIS_DONE`), treatment auto-place, and gallery
 *  accept (`COMMIT_PLACEMENT_SIM`). Guided chrome skips `analysisMatchesScene`
 *  so the gallery can preview; advanced requires it. Graduation is chrome
 *  only (`SET_EDITOR_MODE`) — if accept does not go through here, advanced
 *  hides the sim the user just picked.
 *
 *  Sets `scannedToken = token` and clears both stale flags. Does not bump
 *  `token` or `scanNonce` (those are Compute). */
function writeCurrentAnalysis(
  state: EditorState,
  patch: Partial<Pick<EditorState['analysis'], 'freq' | 'rt60' | 'soundstage' | 'lastSolveMs' | 'viz'>>,
): EditorState {
  return {
    ...state,
    analysis: {
      ...state.analysis,
      loading: false,
      error: null,
      scannedToken: state.analysis.token,
      beforeStale: false,
      afterStale: false,
      afterResultVersion: state.analysis.afterResultVersion + 1,
      ...patch,
    },
  };
}

function rt60ResponseFromTierResult(result: TierResult): RT60Response | null {
  const after = result.rt60After;
  const keys = Object.keys(after);
  if (keys.length === 0) return null;
  const freqs = keys.map(Number).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  return {
    rt60: after,
    rt60_fine: {
      freqs,
      rt60: freqs.map((f) => after[String(f)] ?? 0),
    },
    ...(Object.keys(result.rt60Before).length > 0 ? { rt60_baseline: result.rt60Before } : {}),
  };
}

/** Auto-place already ran a solve — treat the new panels as scanned so the
 *  viewport refetches viz instead of showing "scene changed — Scan to update". */
function applyPlacementSim(state: EditorState, result: TierResult): EditorState {
  const rt60 = rt60ResponseFromTierResult(result);
  return writeCurrentAnalysis(state, rt60 ? { rt60 } : {});
}

/**
 * Drop back into design mode, if we were in physics mode.
 *
 * Physics view paints every object white, which makes a paint pick, a floor
 * finish or a colour swatch do nothing visible at all — the control works,
 * the state changes, and the viewport does not move. Rather than disabling
 * those controls (they are reachable from four different cards) or explaining
 * the situation in a notice nobody reads, an edit whose ONLY observable effect
 * is colour puts the user back where they can see it.
 *
 * Returns the same object when already in design mode, so this can be applied
 * unconditionally without breaking the identity check `editorReducer` uses to
 * spot a no-op re-dispatch.
 */
function showingDesign(view: EditorState['view']): EditorState['view'] {
  return view.renderMode === 'physics' ? { ...view, renderMode: 'design' } : view;
}

// Mirrors floorplan-core's NOGO_MIN_SIZE_M -- duplicated rather than
// imported because floorplan-core depends on THIS package for its types
// (NoGoZone, RoomDims), so the reverse import would be a cycle.
const NOGO_MIN_SIZE_M = 0.2;

/** Re-clamp every zone to the room's new `[0, Lx] x [0, Ly]` box after a
 *  resize. A zone is created polygon-clamped to the room it was drawn in
 *  (floorplan-core's `clampRectToRoom`, called from `draftToNoGoZone`), but
 *  `SET_DIMS` only ever touched `doc.dims` -- shrink the room afterward and
 *  the zone's stored coordinates kept pointing at the OLD, larger box, so a
 *  wall drawn no-go zone could end up sitting partly or entirely outside the
 *  room the solver actually sees. `search_layout` never trusted an
 *  exclusion zone's bounds against Lx/Ly either (only against the room's
 *  interior grid downstream), so this had nowhere else to get caught.
 *
 *  Axis-aligned box only, not the true footprint polygon: SET_DIMS never
 *  changes `footprint`/`wings`, and a non-rectangular room's zones are a
 *  pre-existing, separate concern (SET_FOOTPRINT/SET_WINGS), not something
 *  this action newly breaks. A zone that no longer overlaps the box at all,
 *  or clamps below the drawing tool's own minimum size, is dropped. */
function clampNoGoZonesToDims(zones: NoGoZone[], Lx: number, Ly: number): NoGoZone[] {
  const out: NoGoZone[] = [];
  for (const z of zones) {
    const x0 = Math.max(0, Math.min(z.x0, Lx));
    const y0 = Math.max(0, Math.min(z.y0, Ly));
    const x1 = Math.max(0, Math.min(z.x1, Lx));
    const y1 = Math.max(0, Math.min(z.y1, Ly));
    if (x1 - x0 >= NOGO_MIN_SIZE_M && y1 - y0 >= NOGO_MIN_SIZE_M) {
      out.push({ ...z, x0, y0, x1, y1 });
    }
  }
  return out.length === zones.length
    && out.every((z, i) => z.x0 === zones[i]!.x0 && z.y0 === zones[i]!.y0
      && z.x1 === zones[i]!.x1 && z.y1 === zones[i]!.y1)
    ? zones          // nothing actually changed -- keep the same reference
    : out;
}

// Mirrors floorplan-core's MIN_CEILING_NODE_M -- duplicated rather than
// imported for the same reason NOGO_MIN_SIZE_M above is: floorplan-core
// depends on core, so the reverse import would be a cycle.
const MIN_CEILING_NODE_M = 0.01;

/** Shift every ceiling-profile node by `deltaH` metres.
 *
 *  A ceiling profile's nodes store ABSOLUTE height in metres (`CeilingNode
 *  = [t, z]`) -- there is no reference back to `dims.H` at all. A flat
 *  ceiling (no profile) tracks `H` live because `ceilingHeightAtWorld`
 *  falls back to `H` directly when there's no slope, but the INSTANT a
 *  slope exists (a roof preset, or a hand-dragged node), its heights are
 *  frozen at whatever they were when set -- changing the room's Height
 *  slider afterward moved `dims.H` and left every sloped ceiling exactly
 *  where it was, in both the 2D profile editor and the 3D room. This is
 *  the fix: `SET_DIMS`/`RESIZE_ROOM` shift the whole profile by the same
 *  delta the room's height changed by, so a shed/vault/cathedral/barrel
 *  roof rises and falls WITH the room instead of floating at its
 *  original absolute height. Clamped to MIN_CEILING_NODE_M so a large
 *  height decrease can't push a node through (or below) the floor.
 *
 *  Shifting BOTH axes by the same delta preserves `ceilingHeightAt`'s
 *  secondary-axis contract (`zp + (zs - zsMax)`) exactly: that expression
 *  only ever depends on DIFFERENCES between an axis's own node heights, so
 *  adding the same constant to every node on every axis cancels out of
 *  every difference and changes nothing but the shared absolute reference. */
function shiftCeilingHeight(ceiling: CeilingProfile | null, deltaH: number): CeilingProfile | null {
  if (!ceiling || Math.abs(deltaH) < 1e-9) return ceiling;
  const shiftAxis = (ap: AxisProfile | null): AxisProfile | null => {
    if (!ap) return ap;
    return {
      ...ap,
      nodes: ap.nodes.map(([t, z]) => [t, Math.max(MIN_CEILING_NODE_M, z + deltaH)] as CeilingNode),
    };
  };
  return { ...ceiling, x: shiftAxis(ceiling.x), y: shiftAxis(ceiling.y) };
}

export function coreReducer(state: EditorState, action: CoreAction): EditorState {
  switch (action.type) {
    case 'SET_DIMS': {
      const dims = { ...state.doc.dims, ...action.dims };
      return {
        ...state,
        doc: {
          ...state.doc,
          dims,
          ceiling: shiftCeilingHeight(state.doc.ceiling, dims.H - state.doc.dims.H),
          noGoZones: clampNoGoZonesToDims(state.doc.noGoZones, dims.Lx, dims.Ly),
        },
      };
    }

    // Atomic room-dimension edit: dims + (optionally) a rescaled footprint +
    // every panel/furniture item that has to move with a wall, ceiling, or
    // corner that just relocated -- ALL as one dispatch, so it is also
    // exactly one undo step. RoomPanel.tsx computes `panels`/`furniture` in
    // the web layer (it needs panel-surface's THREE-based surface math,
    // which core cannot depend on -- see panel-surface's own note on why
    // the dependency runs the other way); this case only ever mechanically
    // applies the precomputed result. Before this action existed, RoomPanel
    // dispatched SET_DIMS + SET_FOOTPRINT + one SYNC_TRANSFORM per affected
    // object, which meant a single width-slider drag left a trail of
    // separate undo steps and, worse, `readPanelSurface` was re-run against
    // whatever `doc` HAPPENED to be current in the closure at dispatch time
    // rather than a single consistent before/after pair -- see git history
    // for the exact "panels ended up on the floor" symptom that produced.
    case 'RESIZE_ROOM': {
      const dims = { ...state.doc.dims, ...action.dims };
      const panelPatch = new Map(action.panels?.map((p) => [p.id, p]));
      const furniturePatch = new Map(action.furniture?.map((f) => [f.id, f]));
      const speakerPatch = new Map(action.speakers?.map((sp) => [sp.id, sp]));
      return {
        ...state,
        doc: {
          ...state.doc,
          dims,
          footprint: action.footprint !== undefined ? action.footprint : state.doc.footprint,
          ceiling: shiftCeilingHeight(state.doc.ceiling, dims.H - state.doc.dims.H),
          noGoZones: clampNoGoZonesToDims(state.doc.noGoZones, dims.Lx, dims.Ly),
          panels: state.doc.panels.map((p) => {
            const patch = panelPatch.get(p.id);
            return patch ? { ...p, position: patch.position, rotation: patch.rotation } : p;
          }),
          furniture: state.doc.furniture.map((f) => {
            const patch = furniturePatch.get(f.id);
            return patch ? { ...f, ...patch } : f;
          }),
          // The caller computes these positions, so the reducer still owns
          // the floor rule for them (see `seatOnFloor`).
          speakers: state.doc.speakers.map((sp) => {
            const patch = speakerPatch.get(sp.id);
            return patch ? seatOnFloor({ ...sp, position: patch.position }) : sp;
          }),
          listener: state.doc.listener && action.listener
            ? { ...state.doc.listener, position: action.listener.position }
            : state.doc.listener,
        },
      };
    }

    case 'SET_MATERIAL':
      return {
        ...state,
        doc: action.surface === 'floor'
          ? { ...state.doc, floorMaterial: action.key }
          : { ...state.doc, wallMaterial: action.key },
      };

    case 'SET_FURNISHING':
      return { ...state, doc: { ...state.doc, furnishing: action.furnishing } };

    case 'SET_PIPELINE':
      if (action.pipeline === state.settings.pipeline) return state;
      return { ...state, settings: { ...state.settings, pipeline: action.pipeline } };

    case 'SET_UNITS':
      return { ...state, settings: { ...state.settings, units: action.units } };

    case 'ADD_SPEAKER': {
      const entry: SpeakerEntry = seatOnFloor({
        id: genId(),
        subkind: action.subkind,
        role: action.role ?? null,
        freqResponse: action.freqResponse ?? null,
        position: action.position ?? defaultSpeakerPos(
          state.doc.dims, action.subkind, state.doc.listener?.position[1],
        ),
        rotationY: action.rotationY ?? 0,
        color: action.color,
        faceListener: action.faceListener,
      });
      return {
        ...state,
        doc: { ...state.doc, speakers: [...state.doc.speakers, entry] },
        selection: { id: entry.id, kind: 'speaker' },
      };
    }

    case 'DUPLICATE_SPEAKER': {
      const src = state.doc.speakers.find((s) => s.id === action.id);
      if (!src) return state;
      const entry: SpeakerEntry = seatOnFloor({
        ...src,
        id: genId(),
        role: null,
        position: [src.position[0] + 0.4, src.position[1], src.position[2]],
      });
      return {
        ...state,
        doc: { ...state.doc, speakers: [...state.doc.speakers, entry] },
        selection: { id: entry.id, kind: 'speaker' },
      };
    }

    case 'ADD_LISTENER': {
      const d = state.doc.dims;
      const entry: ListenerEntry = {
        id: genId(),
        position: action.position ?? [d.Lx / 2, 1.2, d.Ly * 0.68],
        // Forward: the doc's own facing, else the front wall at z = 0 — the
        // convention every speaker layout is generated in (room-flow's
        // generateSpeakerLayout, av-compat's forwardVector). It was +X, so a
        // seat added before its speakers faced the side wall: its arrow
        // pointed away from the layout built in front of it, and everything
        // read relative to where it faces (channel names, the mirror line of
        // a stereo pair) came out wrong until a placement solve re-aimed it.
        rotationY: action.rotationY ?? state.doc.facingYawRad ?? Math.PI,
      };
      return {
        ...state,
        doc: { ...state.doc, listener: entry },
        selection: { id: entry.id, kind: 'listener' },
      };
    }

    case 'REMOVE_OBJECT': {
      const { doc, selection } = state;
      const id = action.id;
      const nextSpeakers = doc.speakers.filter((s) => s.id !== id);
      const nextListener = doc.listener?.id === id ? null : doc.listener;
      const nextPanels = doc.panels.filter((p) => p.id !== id);
      const nextFurniture = doc.furniture.filter((f) => f.id !== id);
      const nextWindows = (doc.windows ?? []).filter((w) => w.id !== id);
      const nextDoors = (doc.doors ?? []).filter((d) => d.id !== id);
      const nextMedia = (doc.media ?? []).filter((m) => m.id !== id);
      const nextGear = doc.gear?.filter((g) => g.id !== id);
      return {
        ...state,
        doc: {
          ...doc,
          speakers: nextSpeakers,
          listener: nextListener,
          panels: nextPanels,
          furniture: nextFurniture,
          windows: nextWindows,
          doors: nextDoors,
          media: nextMedia,
          ...(nextGear ? { gear: nextGear } : {}),
        },
        selection: selection?.id === id ? null : selection,
      };
    }

    case 'SET_SPEAKER_ROLE':
      return {
        ...state,
        doc: {
          ...state.doc,
          speakers: state.doc.speakers.map((s) =>
            s.id === action.id ? { ...s, role: action.role } : s,
          ),
        },
      };

    case 'UPDATE_SPEAKER':
      return {
        ...state,
        doc: {
          ...state.doc,
          speakers: state.doc.speakers.map((s) =>
            s.id === action.id ? reshapeSpeaker(s, action.patch, state.doc) : s,
          ),
        },
        // Cabinet finish only — see UPDATE_PANEL above.
        view: action.patch.color !== undefined ? showingDesign(state.view) : state.view,
      };

    case 'SET_SPEAKER_SETUP': {
      // This case was MISSING. The action was declared in `EditorAction`,
      // dispatched from StepSpeakers on mount and on every control change, and
      // fell straight through to `default: return state` — so choosing a layout
      // did nothing, and a fresh room reached the solve with zero speakers. It
      // type-checked the whole time: a switch with a `default` is never checked
      // for exhaustiveness, so an action can be declared, dispatched and
      // silently dropped with no error anywhere. `reducerCoverage.test.ts` is
      // what makes that impossible now.
      //
      // The speaker list is REPLACED rather than merged: `generateSpeakerLayout`
      // returns the whole arrangement for the chosen layout, and merging would
      // strand speakers from a larger previous layout in the room.
      const ids = new Set(action.speakers.map((s) => s.id));
      return {
        ...state,
        doc: {
          ...state.doc,
          speakerSetup: action.setup,
          speakers: action.speakers.map(seatOnFloor),
        },
        // A selected speaker that the new layout does not contain is gone; a
        // selection naming it would leave the inspector editing a ghost.
        selection: state.selection?.kind === 'speaker' && !ids.has(state.selection.id)
          ? null
          : state.selection,
      };
    }

    case 'ADD_PANEL': {
      const entry: PanelEntry = { id: genId(), ...action.panel };
      return {
        ...state,
        doc: { ...state.doc, panels: [...state.doc.panels, entry] },
        selection: { id: entry.id, kind: 'panel' },
      };
    }

    case 'UPDATE_PANEL':
      return {
        ...state,
        doc: {
          ...state.doc,
          panels: state.doc.panels.map((p) =>
            p.id === action.id ? { ...p, ...action.patch } : p,
          ),
        },
        // Only a COLOUR patch drops out of physics view. Moving, resizing or
        // re-seating a panel is perfectly legible as a white slab and is
        // exactly the kind of edit physics view exists for — flipping the
        // room back to design mode on every drag would make the mode
        // unusable for the one job it has.
        view: action.patch.color !== undefined ? showingDesign(state.view) : state.view,
      };

    case 'REVEAL_PANELS':
      return { ...state, doc: { ...state.doc, panels: action.panels } };

    case 'ADD_FURNITURE': {
      const entry: FurnitureEntry = { id: genId(), ...action.furniture };
      return {
        ...state,
        doc: { ...state.doc, furniture: [...state.doc.furniture, entry] },
        selection: { id: entry.id, kind: 'furniture' },
      };
    }

    case 'UPDATE_FURNITURE':
      return {
        ...state,
        doc: {
          ...state.doc,
          furniture: state.doc.furniture.map((f) =>
            f.id === action.id ? { ...f, ...action.patch } : f,
          ),
        },
      };

    case 'ADD_MEDIA': {
      const entry: MediaEntry = {
        ...action.media,
        id: genId(),
        diagonalM: clampDiagonalM(action.media.diagonalM),
      };
      return {
        ...state,
        doc: { ...state.doc, media: [...(state.doc.media ?? []), entry] },
        selection: { id: entry.id, kind: 'media' },
        // Adding a TV from the surface card takes the inspector off the wall
        // and onto the thing just placed — same handoff ADD_WINDOW does.
        view: { ...state.view, selectedSurface: null },
      };
    }

    case 'SET_SPEAKER_PRODUCT': {
      const ids = new Set(action.speakerIds);
      const { product } = action;
      if (!state.doc.speakers.some((s) => ids.has(s.id))) return state;
      return {
        ...state,
        doc: {
          ...state.doc,
          speakers: state.doc.speakers.map((s) => (ids.has(s.id) ? bindSpeakerProduct(state.doc, s, product) : s)),
        },
      };
    }

    case 'ADD_SPEAKERS': {
      if (action.speakers.length === 0) return state;
      return {
        ...state,
        doc: {
          ...state.doc,
          speakerSetup: action.setup,
          speakers: [...state.doc.speakers, ...action.speakers.map(seatOnFloor)],
        },
        selection: { id: action.speakers[0].id, kind: 'speaker' },
      };
    }

    case 'ADD_GEAR': {
      const entry: GearEntry = { ...action.gear, id: genId() };
      const kept = (state.doc.gear ?? []).filter((g) => g.id !== action.replaceId);
      return {
        ...state,
        doc: { ...state.doc, gear: [...kept, entry] },
        selection: { id: entry.id, kind: 'gear' },
        view: { ...state.view, selectedSurface: null },
      };
    }

    case 'ADOPT_GEAR_DISPLAYS': {
      const doc = { ...state.doc, gear: action.gear, media: action.media };
      // A selected display that was gear is now a media entry with another id.
      const gone = state.selection && objectKindOf(doc, state.selection.id) === null;
      return { ...state, doc, selection: gone ? null : state.selection };
    }

    case 'SET_CHECKLIST_ITEM': {
      const done = state.doc.checklist?.done ?? [];
      const has = done.includes(action.itemId);
      if (has === action.done) return state;
      const next = action.done ? [...done, action.itemId] : done.filter((d) => d !== action.itemId);
      return { ...state, doc: { ...state.doc, checklist: { done: next } } };
    }

    case 'SET_LISTENING_LEVEL': {
      const peakDb = Math.min(115, Math.max(70, action.peakDb));
      if (state.doc.listening?.peakDb === peakDb) return state;
      return { ...state, doc: { ...state.doc, listening: { peakDb } } };
    }

    case 'SET_ORDER_LINE': {
      const lines = { ...(state.doc.order?.lines ?? {}) };
      const next: OrderLine = { ...(lines[action.key] ?? {}) };
      const p = action.patch;
      if ('where' in p) { if (p.where) next.where = p.where; else delete next.where; }
      for (const k of ['lotUsd', 'promoUsd', 'shippingUsd'] as const) {
        if (!(k in p)) continue;
        const v = moneyOrNull(p[k]);
        if (v === undefined) delete next[k]; else next[k] = v;
      }
      if (Object.keys(next).length === 0) delete lines[action.key];
      else lines[action.key] = next;
      if (JSON.stringify(lines) === JSON.stringify(state.doc.order?.lines ?? {})) return state;
      return { ...state, doc: { ...state.doc, order: { ...(state.doc.order ?? {}), lines } } };
    }

    case 'SET_ORDER_TAX': {
      const pct = action.pct === null || !Number.isFinite(action.pct)
        ? undefined
        : Math.round(Math.min(MAX_TAX_PCT, Math.max(0, action.pct)) * 1000) / 1000;
      if ((state.doc.order?.taxPct ?? undefined) === pct) return state;
      const order: OrderState = { ...(state.doc.order ?? {}) };
      if (pct === undefined) delete order.taxPct; else order.taxPct = pct;
      return { ...state, doc: { ...state.doc, order } };
    }

    case 'SET_BUDGET': {
      const usd = action.usd === null || !Number.isFinite(action.usd)
        ? null
        : Math.round(Math.min(MAX_BUDGET_USD, Math.max(0, action.usd)));
      if ((state.doc.budget?.usd ?? null) === usd) return state;
      return { ...state, doc: { ...state.doc, budget: usd === null ? null : { usd } } };
    }

    case 'UPDATE_MEDIA':
      return {
        ...state,
        doc: {
          ...state.doc,
          media: (state.doc.media ?? []).map((m) => {
            if (m.id !== action.id) return m;
            const next = { ...m, ...action.patch };
            // Re-clamp on every patch, not just the ones naming a diagonal:
            // a typed-in size arrives here as an arbitrary number, and NaN
            // geometry renders as nothing at all — very hard to trace back.
            return { ...next, diagonalM: clampDiagonalM(next.diagonalM) };
          }),
        },
      };

    case 'SET_ROOM_SHAPE':
      return { ...state, doc: { ...state.doc, roomShape: action.shape } };

    case 'SET_FOOTPRINT': {
      const doc = { ...state.doc, footprint: action.footprint };
      return { ...state, doc: reseatCornerTraps(doc) };
    }

    case 'SET_WINGS':
      return { ...state, doc: { ...state.doc, wings: action.wings } };

    case 'SET_CEILING':
      return { ...state, doc: { ...state.doc, ceiling: action.ceiling } };

    case 'SET_NOGO_ZONES':
      return { ...state, doc: { ...state.doc, noGoZones: action.zones } };

    // Shift the whole room and everything standing in it by the same offset.
    //
    // A room's coordinates run from the origin, so a footprint corner dragged
    // past it is not representable — but "you cannot drag left or up" is not an
    // acceptable editor either. The way out is to move the origin instead of
    // refusing the gesture: translate the footprint back into positive space and
    // carry the speakers, listener, panels, furniture and no-go zones with it.
    // Everything keeps its position RELATIVE to the room, which is the only
    // thing that was ever physically meaningful; only the arbitrary choice of
    // where (0, 0) sits changes.
    case 'TRANSLATE_SCENE': {
      const { dx, dy } = action;
      if (dx === 0 && dy === 0) return state;
      const shift3 = (p: Vec3): Vec3 => [p[0] + dx, p[1], p[2] + dy];
      const d = state.doc;
      return {
        ...state,
        doc: {
          ...d,
          footprint: d.footprint
            ? d.footprint.map((c) => ({ ...c, x: c.x + dx, y: c.y + dy }))
            : null,
          speakers: d.speakers.map((s) => ({ ...s, position: shift3(s.position) })),
          listener: d.listener
            ? { ...d.listener, position: shift3(d.listener.position) }
            : null,
          panels: d.panels.map((p) => ({ ...p, position: shift3(p.position) })),
          furniture: d.furniture.map((f) => ({ ...f, position: shift3(f.position) })),
          // The screen's own placement is (u, v) ON its host wall, which is
          // relative to the wall and so is unaffected by re-origining the room.
          media: (d.media ?? []).map((m) => ({ ...m, position: shift3(m.position) })),
          noGoZones: d.noGoZones.map((z) => ({
            ...z, x0: z.x0 + dx, x1: z.x1 + dx, y0: z.y0 + dy, y1: z.y1 + dy,
          })),
        },
      };
    }

    case 'SELECT': {
      if (!action.id) {
        return {
          ...state,
          selection: null,
          view: state.view.selectedSurface === null
            ? state.view
            : { ...state.view, selectedSurface: null },
        };
      }
      // Look the kind UP. This used to hardcode `kind: 'speaker'` for every
      // selection, which meant clicking a panel recorded it as a speaker —
      // `usePanelPlacement` gates on `selection.kind === 'panel'`, found
      // 'speaker', returned null, and the panel's inspector card vanished the
      // instant you clicked the panel. Adding a panel still worked, because
      // ADD_PANEL sets the kind correctly; only clicking one was broken, which
      // is why it read as "selection does nothing" rather than as a type bug.
      const kind = objectKindOf(state.doc, action.id);
      return {
        ...state,
        // An id that matches nothing in the document is not a selection. Better
        // to clear than to invent a kind and have the UI act on a stale id.
        selection: kind ? { id: action.id, kind } : null,
        // Selecting an object hands the inspector back from any painted
        // surface — the two cards share one slot and must not both claim it.
        view: state.view.selectedSurface === null
          ? state.view
          : { ...state.view, selectedSurface: null },
      };
    }

    case 'SYNC_TRANSFORM': {
      // Same-pose no-op: previewing the already-applied card (template 1-option,
      // or Accept of the card currently on screen) must not bump `token` or
      // null freq. Returning `state` lets editorReducer skip history/staleness
      // (`if (nextState === state) return`). Do not delete these equality
      // checks to "simplify" — that reintroduces "Scene changed" on rooms
      // that were only re-previewed.
      const { id, position, rotationY, rotation } = action;
      if (state.doc.listener?.id === id) {
        const moved = moveListenerTo(state.doc.listener, position, rotationY);
        if (vec3Eq(state.doc.listener.position, position) && state.doc.listener.rotationY === moved.rotationY) {
          return state;
        }
        return { ...state, doc: { ...state.doc, listener: moved } };
      }
      const speaker = state.doc.speakers.find((s) => s.id === id);
      if (speaker) {
        const moved = moveSpeakerTo(speaker, position, state.doc.listener, rotationY);
        // Compare the pose the speaker would actually take, not the one asked
        // for: a floor-standing cabinet refuses a height, and a move that only
        // asked for one must stay a no-op (no undo step, no stale result).
        if (vec3Eq(speaker.position, moved.position) && speaker.rotationY === moved.rotationY) {
          return state;
        }
      } else {
        const panel = state.doc.panels.find((p) => p.id === id);
        if (panel) {
          const nextRot = rotation ?? panel.rotation;
          if (vec3Eq(panel.position, position) && rot3Eq(panel.rotation, nextRot)) return state;
        } else {
          const furniture = state.doc.furniture.find((f) => f.id === id);
          if (furniture) {
            const nextRot = rotation ?? furniture.rotation;
            if (vec3Eq(furniture.position, position) && rot3Eq(furniture.rotation, nextRot)) return state;
          } else {
            const media = (state.doc.media ?? []).find((m) => m.id === id);
            if (media) {
              const nextRot = rotation ?? media.rotation;
              if (vec3Eq(media.position, position) && rot3Eq(media.rotation, nextRot)) return state;
            } else {
              const gear = (state.doc.gear ?? []).find((g) => g.id === id);
              if (!gear) return state;
              if (vec3Eq(gear.position, position) && (rotationY ?? gear.rotationY) === gear.rotationY) return state;
            }
          }
        }
      }
      return {
        ...state,
        doc: {
          ...state.doc,
          // Locked (the default): yaw is a function of position and the
          // listener's, recomputed on every move — an explicit rotationY
          // is ignored while locked, same as the checkbox promises.
          speakers: state.doc.speakers.map((s) =>
            s.id === id ? moveSpeakerTo(s, position, state.doc.listener, rotationY) : s,
          ),
          panels: state.doc.panels.map((p) =>
            p.id === id
              ? { ...p, position, rotation: rotation ?? p.rotation }
              : p,
          ),
          furniture: state.doc.furniture.map((f) =>
            f.id === id
              ? { ...f, position, rotation: rotation ?? f.rotation }
              : f,
          ),
          media: (state.doc.media ?? []).map((m) =>
            m.id === id
              ? { ...m, position, rotation: rotation ?? m.rotation }
              : m,
          ),
          ...(state.doc.gear
            ? {
                gear: state.doc.gear.map((g) =>
                  g.id === id ? { ...g, position, rotationY: rotationY ?? g.rotationY } : g,
                ),
              }
            : {}),
        },
      };
    }

    case 'SET_EDITOR_MODE':
      return { ...state, doc: { ...state.doc, editorMode: action.mode } };

    case 'SET_GUIDED_STEP':
      return { ...state, doc: { ...state.doc, guidedStep: action.step } };

    case 'FORK_ITERATION': {
      const sourceRecord = state.iterations.items.find((it) => it.id === action.fromId);
      if (!sourceRecord) return state;
      // Guard against creating a second fork of the same source while the
      // first one is still local (not yet round-tripped to the backend and
      // rebound to a real id) — never a legitimate double-click (a human
      // can't click "fork" twice before the first fork even finishes
      // rendering as the new active tab). Belt-and-suspenders alongside the
      // backend's own (scene, client_id) idempotency (see persistDesigns.ts)
      // — that's the real defense against a duplicate create actually
      // minting a second backend row; this just avoids even asking. The
      // 2026-08-22 "several orphaned rows from one click" incident this
      // guard was originally written for turned out to be a genuine
      // ResizeObserver self-retrigger loop in ChromeSeam.tsx repeatedly
      // tripping React's "Maximum update depth exceeded" and resetting
      // reducer state mid-fork — fixed there, not here.
      const active = state.iterations.items.find((it) => it.id === state.iterations.activeId);
      if (active && active.parentId === action.fromId && !/^\d+$/.test(active.id)) {
        return state;
      }
      const sourceDoc = action.fromId === state.iterations.activeId ? state.doc : sourceRecord.doc;
      const sourceAnalysis = action.fromId === state.iterations.activeId
        ? state.analysis
        : sourceRecord.analysis;
      const sourceTreatment = action.fromId === state.iterations.activeId
        ? state.treatment
        : sourceRecord.treatment;
      const nextSort = state.iterations.items.reduce((m, it) => Math.max(m, it.sortOrder), -1) + 1;
      const existing = state.iterations.items.map((it) => it.label);
      const label = action.label?.trim()
        ? uniqueLabel(action.label.trim(), existing)
        : copyLabel(sourceRecord.label, existing);
      // A fork is created from inside the editor, never onboarding -- land on
      // it in advanced mode regardless of what mode the source doc was in
      // (resumeRoomMode() checks editorMode before guidedStep).
      const forked = makeIterationDoc({
        id: genId(),
        label,
        parentId: action.fromId,
        doc: { ...cloneDoc(sourceDoc), editorMode: 'advanced' },
        history: EMPTY_HISTORY,
        analysis: sourceAnalysis,
        treatment: sourceTreatment,
        docRev: 0,
        resultsRev: sourceAnalysis.freq || sourceAnalysis.rt60 ? sourceRecord.docRev : null,
        sortOrder: nextSort,
      });
      const items = [...snapshotActive(state), forked];
      return activateDesign(state, items, forked.id);
    }

    case 'ADD_BLANK_ITERATION': {
      const nextSort = state.iterations.items.reduce((m, it) => Math.max(m, it.sortOrder), -1) + 1;
      const existing = state.iterations.items.map((it) => it.label);
      const desired = (action.label ?? nextBlankLabel(existing)).trim()
        || nextBlankLabel(existing);
      // Same as FORK_ITERATION above -- a blank Design added from the tab
      // strip should never drop the user into onboarding.
      const blank = makeIterationDoc({
        id: genId(),
        label: uniqueLabel(desired, existing),
        parentId: null,
        doc: { ...createBlankDoc(), editorMode: 'advanced' },
        history: EMPTY_HISTORY,
        sortOrder: nextSort,
      });
      const items = [...snapshotActive(state), blank];
      return activateDesign(state, items, blank.id);
    }

    case 'SWITCH_ITERATION': {
      if (action.id === state.iterations.activeId) return state;
      if (!state.iterations.items.some((it) => it.id === action.id)) return state;
      const items = snapshotActive(state);
      return activateDesign(state, items, action.id);
    }

    case 'DELETE_ITERATION': {
      if (state.iterations.items.length <= 1) return state;
      const doomed = state.iterations.items.find((it) => it.id === action.id);
      if (!doomed) return state;
      const items = snapshotActive(state)
        .filter((it) => it.id !== action.id)
        .map((it) => (it.parentId === action.id ? { ...it, parentId: doomed.parentId } : it));
      if (state.iterations.activeId !== action.id) {
        return { ...state, iterations: { items, activeId: state.iterations.activeId } };
      }
      const fallback = items.reduce((best, it) =>
        it.sortOrder < best.sortOrder ? it : best,
      items[0]!);
      return activateDesign(state, items, fallback.id);
    }

    case 'RENAME_ITERATION': {
      const label = action.label.trim();
      if (!label) return state;
      let changed = false;
      const items = state.iterations.items.map((it) => {
        if (it.id !== action.id || it.label === label) return it;
        changed = true;
        return { ...it, label };
      });
      if (!changed) return state;
      return { ...state, iterations: { ...state.iterations, items } };
    }

    case 'DISCARD_PENDING_RESULT': {
      const target = state.iterations.items.find((it) => it.id === action.designId);
      if (!target || !target.pendingResult) return state;
      const items = state.iterations.items.map((it) =>
        it.id === action.designId ? { ...it, pendingResult: null } : it,
      );
      return { ...state, iterations: { ...state.iterations, items } };
    }

    case 'REBIND_ITERATION': {
      if (action.localId === action.backendId) return state;
      if (!state.iterations.items.some((it) => it.id === action.localId)) return state;
      const items = state.iterations.items.map((it) => ({
        ...it,
        id: it.id === action.localId ? action.backendId : it.id,
        parentId: it.parentId === action.localId ? action.backendId : it.parentId,
      }));
      const activeId = state.iterations.activeId === action.localId
        ? action.backendId
        : state.iterations.activeId;
      return { ...state, iterations: { items, activeId } };
    }

    case 'SYNC_DESIGN_REVISIONS': {
      let changed = false;
      const items = state.iterations.items.map((it) => {
        if (it.id !== action.id) return it;
        if (it.docRev === action.docRev && it.resultsRev === action.resultsRev) return it;
        changed = true;
        return { ...it, docRev: action.docRev, resultsRev: action.resultsRev };
      });
      if (!changed) return state;
      return { ...state, iterations: { ...state.iterations, items } };
    }

    case 'REORDER_ITERATION': {
      const fromIndex = state.iterations.items.findIndex((it) => it.id === action.id);
      if (fromIndex === -1) return state;
      const toIndex = Math.max(0, Math.min(action.toIndex, state.iterations.items.length - 1));
      if (fromIndex === toIndex) return state;
      const items = [...state.iterations.items];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...state, iterations: { ...state.iterations, items } };
    }

    case 'APPLY_SCAN':
      // The panels, media and per-wall overrides a selection could point at
      // are gone with the old shell, so nothing stays selected.
      return {
        ...state,
        doc: applyScannedGeometry(state.doc, normalizeEditorDoc(action.scanned)),
        selection: null,
        view: { ...state.view, selectedSurface: null },
      };

    case 'HYDRATE': {
      const doc = normalizeEditorDoc(action.doc);
      if (action.iterations && action.iterations.items.length > 0) {
        const items = action.iterations.items.map((it) => makeIterationDoc(it));
        const activeId = items.some((it) => it.id === action.iterations!.activeId)
          ? action.iterations.activeId
          : items[0]!.id;
        return activateDesign(state, items, activeId);
      }
      const analysis = analysisFromResults(action.results);
      const treatment = action.treatment
        ? cloneTreatment(action.treatment)
        : cloneTreatment(EMPTY_TREATMENT);
      const root = makeIterationDoc({
        id: ROOT_ITERATION_ID,
        label: DESIGN_COPY.defaultLabel,
        parentId: null,
        doc,
        history: EMPTY_HISTORY,
        analysis,
        treatment,
        sortOrder: 0,
      });
      return {
        ...state,
        doc,
        history: EMPTY_HISTORY,
        analysis,
        treatment,
        iterations: { items: [root], activeId: ROOT_ITERATION_ID },
      };
    }

    // ── Restored 2026-08-11 ──────────────────────────────────────────────
    // The thirty-one cases below were lost when this package was extracted
    // from web/ (commit 1a05cb9). They stayed in the `EditorAction` union, so
    // every call site kept type-checking and kept dispatching; they simply
    // fell through to `default: return state` and did nothing. Viz modes did
    // not switch, dialogs did not open, the catalogue never loaded, analysis
    // results never landed, surface colours never changed, and the speaker
    // layout was never created.
    //
    // Recovered verbatim from the pre-extraction reducer rather than rewritten,
    // so this restores the behaviour that shipped rather than a fresh guess at
    // it. `reducerCoverage.test.ts` is what makes a repeat impossible: it reads
    // the action union and the reducer and fails on any action with no branch.
    // ─────────────────────────────────────────────────────────────────────

    case 'ADD_WINDOW': {
      const entry: WindowEntry = syncWindowGeometry({ ...action.window, id: genId() });
      return {
        ...state,
        doc: { ...state.doc, windows: [...(state.doc.windows ?? []), entry] },
        selection: { id: entry.id, kind: 'window' },
        view: { ...state.view, selectedSurface: null },
      };
    }

    case 'ADD_DOOR': {
      const entry: DoorEntry = syncDoorGeometry({ ...action.door, id: genId() });
      return {
        ...state,
        doc: { ...state.doc, doors: [...(state.doc.doors ?? []), entry] },
        selection: { id: entry.id, kind: 'door' },
        view: { ...state.view, selectedSurface: null },
      };
    }

    case 'ANALYSIS_DONE':
      if (action.token !== state.analysis.token) return state; // stale
      return writeCurrentAnalysis(state, {
        freq: action.freq,
        rt60: action.rt60,
        soundstage: action.soundstage,
        lastSolveMs: action.durationMs,
        // `'viz' in action` — not `action.viz ?? ...` — so an explicit
        // `null` (analyze_scene ran but every viz layer failed) replaces
        // the old viz, while an OMITTED field (a caller that didn't run
        // analyze_scene) leaves `analysis.viz` exactly as it was.
        ...('viz' in action ? { viz: action.viz ?? null } : {}),
      });

    case 'COMMIT_PLACEMENT_SIM':
      // Not token-gated: browsing already advanced `token`; this makes the
      // candidate's FR/RT60/soundstage current for THAT layout (the bare
      // room). Live option 2/3 used to fail because accept never wrote this
      // and then generate remounted at index 0 (see StepGenerate.commit).
      return writeCurrentAnalysis(state, {
        freq: action.freq,
        rt60: action.rt60,
        soundstage: action.soundstage,
      });

    case 'ANALYSIS_ERROR':
      if (action.token !== state.analysis.token) return state;
      return { ...state, analysis: { ...state.analysis, loading: false, error: action.error } };

    case 'ANALYSIS_START':
      return { ...state, analysis: { ...state.analysis, loading: true, error: null } };

    case 'APPLY_ROOM_SOLVE':
      return applyRoomSolve(state, action);

    case 'ARM_VOXEL':
      // Captures the geometry signature at the moment voxel was armed.
      // Downstream consumers compare this against the current geometry
      // signature; mismatch = "user changed geometry after arming, the
      // cached eigensolve is stale, do not consume voxel pipeline".
      // Same signature reapplied (e.g. the user clicked Set up speakers
      // twice without changing geometry) is a no-op.
      if (state.analysis.voxelArmedSig === action.signature) return state;
      return { ...state, analysis: { ...state.analysis, voxelArmedSig: action.signature } };

    case 'BUMP_ANALYSIS':
      // Explicit Scan.  Bumps `scanNonce` (the advanced-editor trigger) and
      // `token` together, so the freq/rt60/soundstage pass re-runs in both
      // chromes.  Charts/soundstage retain their last values until the fresh
      // pass resolves; ANALYSIS_START flips `loading` true momentarily.
      return {
        ...state,
        analysis: {
          ...state.analysis,
          token: state.analysis.token + 1,
          scanNonce: state.analysis.scanNonce + 1,
        },
      };

    case 'CATALOG_ERROR':
      return { ...state, catalog: { ...state.catalog, loading: false, error: action.error } };

    case 'CATALOG_LOADED':
      return {
        ...state,
        catalog: { materials: action.materials, products: action.products, loading: false, error: null },
        doc: {
          ...state.doc,
          floorMaterial: state.doc.floorMaterial ?? action.materials.floor.default,
          wallMaterial: state.doc.wallMaterial ?? action.materials.wall.default,
        },
      };

    case 'CATALOG_LOADING':
      return { ...state, catalog: { ...state.catalog, loading: true, error: null } };

    case 'CLOSE_DIALOG':
      return { ...state, ui: { dialog: null } };

    case 'OPEN_DIALOG':
      return { ...state, ui: { dialog: action.dialog } };

    case 'PRODUCTS_UPDATED':
      return { ...state, catalog: { ...state.catalog, products: action.products } };

    case 'REMOVE_WINDOW':
      return {
        ...state,
        doc: {
          ...state.doc,
          windows: (state.doc.windows ?? []).filter((w) => w.id !== action.id),
        },
        selection: state.selection?.id === action.id ? null : state.selection,
      };

    case 'REMOVE_DOOR':
      return {
        ...state,
        doc: {
          ...state.doc,
          doors: (state.doc.doors ?? []).filter((d) => d.id !== action.id),
        },
        selection: state.selection?.id === action.id ? null : state.selection,
      };

    case 'SELECT_SURFACE':
      if (state.view.selectedSurface === action.key) return state;
      return {
        ...state,
        // Picking a surface takes the inspector away from any selected object,
        // so exactly one thing is being edited at a time.
        selection: action.key ? null : state.selection,
        view: { ...state.view, selectedSurface: action.key },
      };

    case 'SET_ACTIVE_REVEAL':
      return applyTierSliceToDoc({
        ...state,
        treatment: { ...state.treatment, activeReveal: action.level },
      });

    case 'SET_ACTIVE_TIER':
      return applyTierSliceToDoc({
        ...state,
        treatment: { ...state.treatment, activeTier: action.tier },
      });

    case 'SET_COMPARE':
      return { ...state, view: { ...state.view, compare: action.compare } };

    case 'SET_DRAGGING':
      return { ...state, view: { ...state.view, dragging: action.dragging } };

    case 'SET_FACING':
      return { ...state, doc: { ...state.doc, facingYawRad: action.yaw } };

    case 'SET_HOVER_FREQ':
      // Same value optimisation — pointermove fires 60+ Hz; skipping
      // identical writes keeps subscribers from churning.
      if (state.view.hoverFreq === action.freq) return state;
      return { ...state, view: { ...state.view, hoverFreq: action.freq } };

    case 'SET_MODAL_FREQ':
      return { ...state, view: { ...state.view, modalFreq: action.freq } };

    case 'SET_VELOCITY_LEVEL':
      return {
        ...state,
        view: { ...state.view, velocityLevel: Math.min(1, Math.max(0, action.level)) },
      };

    case 'SET_MODAL_VIEW':
      return { ...state, view: { ...state.view, modalView: action.view } };

    case 'SET_PLACING_SKU':
      if (state.view.placingSku === action.sku) return state;
      return { ...state, view: { ...state.view, placingSku: action.sku } };

    case 'SET_SPEAKER_CURVE':
      return {
        ...state,
        doc: {
          ...state.doc,
          speakers: state.doc.speakers.map((s) =>
            s.id === action.id ? { ...s, freqResponse: action.curve } : s,
          ),
        },
      };

    case 'SET_SURFACE_COLOR': {
      const next = { ...(state.doc.surfaceColors ?? {}) };
      if (action.color) next[action.key] = action.color;
      else delete next[action.key];
      return {
        ...state,
        doc: { ...state.doc, surfaceColors: next },
        view: showingDesign(state.view),
      };
    }

    case 'SET_SURFACE_MATERIAL': {
      const next = { ...(state.doc.surfaceMaterials ?? {}) };
      if (action.material) next[action.key] = action.material;
      else delete next[action.key];
      return { ...state, doc: { ...state.doc, surfaceMaterials: next } };
    }

    case 'SET_FLOOR_FINISH': {
      // Picking a look sets the physics. The two used to be independent
      // controls that could silently disagree — a floor that looked like oak
      // and solved as heavy carpet, with nothing on screen saying so — and a
      // finish exists precisely to carry both. The material picker still
      // works afterwards, so this is a sensible default, not a lock.
      const acoustic = action.finish ? floorFinishAcoustic(action.finish) : null;
      const materials = { ...(state.doc.surfaceMaterials ?? {}) };
      if (acoustic) materials.floor = acoustic;
      return {
        ...state,
        doc: { ...state.doc, floorFinish: action.finish, surfaceMaterials: materials },
        // A finish is the most dramatic thing physics view hides — it is a
        // whole procedural shader, not a colour — so picking one is the
        // clearest case for showing the user what they just chose.
        view: showingDesign(state.view),
      };
    }

    case 'SET_VIZ':
      return {
        ...state,
        view: { ...state.view, vizMode: action.mode, vizStatus: action.mode === 'none' ? '' : state.view.vizStatus },
      };

    case 'SET_RENDER_MODE':
      // Identity-stable on a same-value dispatch: useLocalSettings re-applies
      // the persisted preference on every mount, and editorReducer treats an
      // unchanged state as "nothing happened" rather than an edit.
      if (state.view.renderMode === action.mode) return state;
      return { ...state, view: { ...state.view, renderMode: action.mode } };

    case 'SET_REFLECTION_FILTER':
      return {
        ...state,
        view: {
          ...state.view,
          reflectionMaxTimeMs: action.maxTimeMs ?? state.view.reflectionMaxTimeMs,
          reflectionOrder: action.order !== undefined ? action.order : state.view.reflectionOrder,
        },
      };

    case 'SET_VIZ_STATUS':
      return { ...state, view: { ...state.view, vizStatus: action.status } };

    case 'TREATMENT_TIER_DONE': {
      const next: EditorState = {
        ...state,
        treatment: {
          ...state.treatment,
          running: { ...state.treatment.running, [action.tier]: false },
          results: { ...state.treatment.results, [action.tier]: action.result },
        },
      };
      // If this completed tier is the active one, push its slice into doc.panels
      // so the viewport updates without a separate user click.
      return action.tier === next.treatment.activeTier
        ? applyTierSliceToDoc(next)
        : next;
    }

    case 'TREATMENT_TIER_ERROR':
      return {
        ...state,
        treatment: {
          ...state.treatment,
          running: { ...state.treatment.running, [action.tier]: false },
          // Surface the most recent failure; the UI clears it on the next start.
          error: action.error,
        },
      };

    case 'TREATMENT_TIER_CANCELLED':
      // The mirror of START, and idempotent on purpose: the Stop button
      // dispatches this synchronously so the pane responds to the press, and
      // the poll loop's own 'Cancelled.' rejection can land up to a poll
      // interval later. Whichever arrives second must be a no-op rather than
      // resurrecting a running flag or a message.
      return {
        ...state,
        treatment: {
          ...state.treatment,
          running: { ...state.treatment.running, [action.tier]: false },
          error: null,
        },
      };

    case 'TREATMENT_TIER_START':
      return {
        ...state,
        treatment: {
          ...state.treatment,
          running: { ...state.treatment.running, [action.tier]: true },
          // Clear any prior result for this tier — re-solving invalidates it.
          results: { ...state.treatment.results, [action.tier]: null },
          // Clear a previous error; a successful start means the user is
          // trying again or this is the first attempt for this tier.
          error: null,
        },
      };

    case 'UPDATE_WINDOW':
      return {
        ...state,
        doc: {
          ...state.doc,
          windows: (state.doc.windows ?? []).map((w) =>
            w.id === action.id
              ? syncWindowGeometry({
                  ...w,
                  ...action.patch,
                  // AABB edit without a new outline drops a stale custom polygon.
                  polygon: action.patch.polygon !== undefined
                    ? action.patch.polygon
                    : (action.patch.u != null || action.patch.v != null
                      || action.patch.w != null || action.patch.h != null)
                    ? undefined
                    : w.polygon,
                })
              : w,
          ),
        },
      };

    case 'UPDATE_DOOR':
      return {
        ...state,
        doc: {
          ...state.doc,
          doors: (state.doc.doors ?? []).map((d) =>
            d.id === action.id ? syncDoorGeometry({ ...d, ...action.patch }) : d,
          ),
        },
      };
    default: {
      // Compile-time exhaustiveness: a new CoreAction without a case fails typecheck.
      const _exhaustive: never = action;
      return state;
    }
  }
}

/** True when Graphs / 3D viz / panel absorption may present simulation
 *  results as matching the current doc. `token === scannedToken` alone is
 *  not enough: UNDO used to force those equal on every step, including
 *  undos that only peeled back one of N edits. `afterStale` is the
 *  treated-room flag (and is also set whenever `beforeStale` is). */
export function analysisMatchesScene(analysis: EditorState['analysis']): boolean {
  return !analysis.afterStale && analysis.token === analysis.scannedToken;
}

/** Job pipeline W2 (2026-09-06), §3.5: Compute is never a silent no-op.
 *  `true` means the Compute button should be enabled and a click should run
 *  `analyze_scene` — either the scene changed since the last scan, there is
 *  no result at all yet, or at least one viz layer (modal/volume/velocity/
 *  raytrace) is missing (never computed, or dropped by a garbage-collected
 *  artifact — see `ArtifactRef`'s header). `false` means every layer is
 *  present and matches the current scene, so a re-solve would reproduce
 *  what's already on screen — ScanButton disables with a tooltip in that
 *  case rather than spending a metered simulation for nothing. */
export function computeIsNeeded(analysis: EditorState['analysis']): boolean {
  if (analysis.beforeStale || analysis.afterStale) return true;
  if (analysis.freq == null) return true;
  const viz = analysis.viz;
  if (!viz) return true;
  return viz.modal == null || viz.volume == null || viz.velocity == null || viz.raytrace == null;
}

/** UNDO/REDO's shared restore: bump `token` (a scene version changed,
 *  whichever direction) so caches keyed on it invalidate. Restore the
 *  popped snapshot's results and staleness flags verbatim. Only catch
 *  `scannedToken` up to the new token when that snapshot was itself
 *  current — i.e. it still matched the doc it travels with. An
 *  intermediate edit's snapshot is already stale; forcing freshness
 *  here is what made one Ctrl+Z after N edits resurrect the sim. */
function restoreAnalysisFor(current: EditorState['analysis'], popped?: EditorState['analysis']): EditorState['analysis'] {
  const bumpedToken = current.token + 1;
  if (!popped) return { ...current, token: bumpedToken };
  const restored = cloneAnalysis(popped);
  const snapshotCurrent = !restored.afterStale && restored.token === restored.scannedToken;
  return {
    ...restored,
    token: bumpedToken,
    scannedToken: snapshotCurrent ? bumpedToken : restored.scannedToken,
  };
}

/** `packages/room-flow/src/placement.ts`'s `seatFromCandidate`, duplicated —
 *  not imported: `core` cannot depend on `room-flow` (room-flow depends on
 *  core; the leaf-package rule — see `faceToward`'s own header comment for
 *  the precedent). Same fallback order: a solved seat from either solver's
 *  field name, else the doc's current listener, else the room's centre. */
function seatFromRoomSolveCandidate(cand: OptimizeCandidate, doc: EditorDoc): Vec3 {
  const solved = cand.pos ?? cand.listener_pos;
  if (solved && solved.length === 3) return solved as Vec3;
  if (doc.listener) return doc.listener.position;
  return [doc.dims.Lx / 2, 1.2, doc.dims.Ly / 2];
}

/** APPLY_ROOM_SOLVE's whole effect. Called from `coreReducer`'s switch, like
 *  any other case — the generic `isUndoable`/`isSimDirtying`/`staleClassFor`
 *  machinery in `editorReducer` pushes the ONE history entry for it (see
 *  `isUndoable`'s `APPLY_ROOM_SOLVE` case) using the pre-edit `state` this
 *  function receives, so no bespoke history bookkeeping is needed here — see
 *  that action's doc comment on `EditorAction` for the 4-step contract this
 *  implements. */
function applyRoomSolve(
  state: EditorState,
  action: Extract<EditorAction, { type: 'APPLY_ROOM_SOLVE' }>,
): EditorState {
  const { candidate, bare, treatment, analysis } = action;

  // Step 1 — move the listener and every speaker, using the exact same
  // transforms SYNC_TRANSFORM applies (moveListenerTo / moveSpeakerTo), just
  // applied directly for every agent in one pass instead of via N separate
  // dispatches. Mirrors applyOptimizeCandidate
  // (packages/room-flow/src/placement.ts:89-112) exactly for the default
  // (faceListener !== false) case — pinned by editorReducerContract's
  // anti-drift test, which imports and compares against that function
  // directly — with one deliberate exception: a floor-standing cabinet keeps
  // its bottom on the floor (moveSpeakerTo's `seatOnFloor`), where
  // applyOptimizeCandidate, like its backend twin `scene_with_candidate`,
  // takes the solver's ear-height Y as given.
  const seat = seatFromRoomSolveCandidate(candidate, state.doc);
  const listener = state.doc.listener
    ? moveListenerTo(state.doc.listener, seat, candidate.listener_yaw_rad ?? state.doc.listener.rotationY)
    : state.doc.listener;
  const wire = candidate.speakers;
  const speakers = wire && wire.length
    ? state.doc.speakers.map((s, i) => moveSpeakerTo(s, wire[i]?.position ?? s.position, listener))
    : state.doc.speakers.map((s) => moveSpeakerTo(s, s.position, listener));

  let next: EditorState = {
    ...state,
    doc: { ...state.doc, listener, speakers },
    analysis: { ...state.analysis, token: state.analysis.token + 1 },
  };

  // Step 2 — the candidate's own bare trio becomes "current": the before
  // side of the comparison, same write COMMIT_PLACEMENT_SIM performs today.
  next = writeCurrentAnalysis(next, { freq: bare.freq, rt60: bare.rt60, soundstage: bare.soundstage });

  // Step 3 — treatment, tier 'low' (hardcoded: the only tier a solve_room
  // job returns). Store the result and make 'low' the active tier — this
  // action IS what makes it current, the same fact every real caller of
  // TREATMENT_TIER_DONE already established with a separate SET_ACTIVE_TIER
  // dispatch first (AutoPlaceSection: SET_ACTIVE_TIER('low') then
  // TREATMENT_TIER_START/DONE) — then applyTierSliceToDoc as
  // TREATMENT_TIER_DONE's coreReducer case does. `treatment: null` (the
  // treatment stage failed) skips this whole step — panels stay untouched,
  // seat/speakers still moved above.
  //
  // Panels landing after the bare snapshot was written is a real "after"
  // edit — the same staleClass a live ADD_PANEL/UPDATE_PANEL produces — so
  // it goes through applyStaleClass('panel') like any other panel-only
  // change. This is deliberately NOT the editorReducer wrapper's
  // applyPlacementSim shortcut used elsewhere for TREATMENT_TIER_DONE
  // (bottom of this file): that shortcut unconditionally re-syncs
  // scannedToken for the advanced editor's auto-place flow, which would
  // wrongly mark a failed evaluate stage (step 4, `analysis: null`) as
  // fresh — exactly the half-updated frame this action exists to prevent.
  if (treatment) {
    next = {
      ...next,
      treatment: {
        ...next.treatment,
        running: { ...next.treatment.running, low: false },
        results: { ...next.treatment.results, low: treatment },
        activeTier: 'low',
      },
    };
    next = applyTierSliceToDoc(next);
    next = {
      ...next,
      analysis: applyStaleClass({ ...next.analysis, token: next.analysis.token + 1 }, 'panel'),
    };
  }

  // Step 4 — the treated trio, when the evaluate stage succeeded: catches
  // scannedToken up so scannedToken === token and analysisMatchesScene is
  // true on landing (no "Scene changed" banner). `analysis: null` means
  // that stage failed; leave the analysis exactly as step 3 left it (nulled,
  // afterStale) so the caller's separate BUMP_ANALYSIS dispatch runs a real
  // scan instead of the editor believing a result nobody computed.
  if (analysis) {
    next = writeCurrentAnalysis(next, { freq: analysis.freq, rt60: analysis.rt60, soundstage: analysis.soundstage });
  }

  return next;
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  // History control stays here so coreReducer can be exhaustive over CoreAction.
  if (action.type === 'UNDO') {
    if (state.history.past.length === 0) return state;
    const previous = state.history.past[state.history.past.length - 1];
    const past = state.history.past.slice(0, -1);
    const future = [{ doc: state.doc, analysis: cloneAnalysis(state.analysis) }, ...state.history.future];
    return {
      ...state,
      doc: previous.doc,
      analysis: restoreAnalysisFor(state.analysis, previous.analysis),
      history: { past, future, coalesceKey: null, coalesceAt: 0 },
    };
  }

  if (action.type === 'REDO') {
    if (state.history.future.length === 0) return state;
    const next = state.history.future[0];
    const future = state.history.future.slice(1);
    const past = [...state.history.past, { doc: state.doc, analysis: cloneAnalysis(state.analysis) }];
    return {
      ...state,
      doc: next.doc,
      analysis: restoreAnalysisFor(state.analysis, next.analysis),
      history: { past, future, coalesceKey: null, coalesceAt: 0 },
    };
  }

  // Job pipeline W3 (2026-09-06), §3.1 "apply by origin, never by whatever
  // is active": APPLY_ROOM_SOLVE / COMMIT_PLACEMENT_SIM carrying an
  // `origin` are routed here BEFORE coreReducer / the generic isUndoable
  // wrapper below ever sees them, so a redirected-elsewhere or parked
  // result never pushes a history entry onto (or otherwise touches) the
  // ACTIVE design — only a same-design, hash-matching result falls through
  // to the normal path underneath, which is byte-identical to pre-W3
  // behavior (including the no-`origin` case, so every existing caller and
  // golden fixture is unaffected).
  if (
    (action.type === 'APPLY_ROOM_SOLVE' || action.type === 'COMMIT_PLACEMENT_SIM')
    && action.origin
  ) {
    const { origin } = action;
    const targetId = origin.designId || state.iterations.activeId;
    const items = state.iterations.items;
    const idx = items.findIndex((it) => it.id === targetId);
    // The design this result was computed for no longer exists (deleted
    // while the job was running) — nothing to apply, nothing to park.
    if (idx === -1) return state;
    const isActive = targetId === state.iterations.activeId;
    const targetDoc = isActive ? state.doc : items[idx]!.doc;
    const hashMatches = !origin.docHash || docHash(targetDoc) === origin.docHash;
    if (!hashMatches) {
      const nextItems = items.map((it, i) =>
        i === idx
          ? { ...it, pendingResult: { origin, payload: action, receivedAt: Date.now() } }
          : it,
      );
      return { ...state, iterations: { ...state.iterations, items: nextItems } };
    }
    if (!isActive) {
      // Run the SAME pure transform (applyRoomSolve / writeCurrentAnalysis
      // via coreReducer) against a shadow state seeded from the target
      // Design's own doc/analysis/treatment, then write the result back
      // into that Design's slot — the live active doc/analysis/treatment
      // are never touched, so no history entry belongs on them either.
      const target = items[idx]!;
      const shadow: EditorState = {
        ...state,
        doc: cloneDoc(target.doc),
        history: target.history,
        analysis: cloneAnalysis(target.analysis),
        treatment: cloneTreatment(target.treatment),
      };
      const applied = coreReducer(shadow, action);
      const nextItems = items.map((it, i) =>
        i === idx
          ? {
              ...it,
              doc: applied.doc,
              analysis: applied.analysis,
              treatment: applied.treatment,
              pendingResult: null,
            }
          : it,
      );
      return { ...state, iterations: { ...state.iterations, items: nextItems } };
    }
    // Active design, matching hash: clear any stale pendingResult on it (a
    // fresh matching result supersedes whatever was parked) and fall
    // through to the normal path below.
    if (items[idx]!.pendingResult) {
      const nextItems = items.map((it, i) => (i === idx ? { ...it, pendingResult: null } : it));
      state = { ...state, iterations: { ...state.iterations, items: nextItems } };
    }
  }

  // Moving or removing gear electronics touches the doc but never the solve.
  const dirty = isSimDirtying(action)
    && !((action.type === 'SYNC_TRANSFORM' || action.type === 'REMOVE_OBJECT')
      && objectKindOf(state.doc, action.id) === 'gear');
  const staleCls = staleClassFor(action, state.doc);
  const nextState = coreReducer(state, action);

  // If this action's own reducer case decided nothing actually changed (a
  // same-value SET_PIPELINE re-dispatched from a localStorage restore is the
  // confirmed real case — see useLocalSettings.ts's mount effect, which
  // unconditionally re-dispatches the persisted pipeline preference on
  // every mount), never bump token/staleness or push undo history for it.
  // Without this, ANY isSimDirtying action re-dispatched with its current
  // value marked the room as edited even though nothing changed — this is
  // what caused "scene changed — Scan to update" to appear on a room that
  // was simply reopened, untouched: token advanced past scannedToken for a
  // "change" that was never real, and only the currently-active Design's
  // live `analysis` gets touched this way, which is also why reopening a
  // multi-Design room only ever showed this on whichever Design was active,
  // never on its siblings.
  if (nextState === state) return nextState;

  if (isUndoable(action)) {
    const key = coalesceKeyFor(action);
    const now = Date.now();
    const canCoalesce = key != null
      && state.history.coalesceKey === key
      && now - state.history.coalesceAt < COALESCE_MS
      && state.history.past.length > 0;

    // Pre-edit doc AND pre-edit results, pushed together — one atomic
    // HistoryItem, so UNDO can never restore geometry without the results
    // that were valid for it (or vice versa).
    const item: HistoryItem = { doc: state.doc, analysis: cloneAnalysis(state.analysis) };
    const past = canCoalesce
      ? state.history.past
      : [...state.history.past.slice(-HISTORY_LIMIT + 1), item];

    const bumpedAnalysis = dirty
      ? { ...nextState.analysis, token: nextState.analysis.token + 1 }
      : nextState.analysis;
    return {
      ...nextState,
      analysis: applyStaleClass(bumpedAnalysis, staleCls),
      history: { past, future: [], coalesceKey: key, coalesceAt: now },
    };
  }

  if (!dirty && staleCls === 'none') return nextState;
  const bumpedAnalysis = dirty
    ? { ...nextState.analysis, token: nextState.analysis.token + 1 }
    : nextState.analysis;
  const bumped: EditorState = {
    ...nextState,
    analysis: applyStaleClass(bumpedAnalysis, staleCls),
  };
  if (action.type === 'TREATMENT_TIER_DONE') {
    return applyPlacementSim(bumped, action.result);
  }
  return bumped;
}
